import Cast, {
  CAST_APP_CATALOG,
  CAST_TILE,
  DEFAULT_CAST_APPS,
  DEFAULT_CAST_TOKEN,
  resolveAppId,
} from '@/helpers/Cast';
import { castAppsForBuilding } from '@/helpers/Cast/buildingApps';
import Registration from '@/helpers/Registration';
import LocalStorage from '@/helpers/StorageHandler/LocalStorage';
import WakeAlarm from '@/helpers/WakeAlarm';
import DeviceType from '@/helpers/DeviceType';
import LgScreen from '@/helpers/LgScreen';
import ForeignApp from '@/helpers/ForeignApp';
import BringToFront from '@/helpers/BringToFront';

// Fortschritt der Firmware-/Clone-Callbacks auf Prozent bringen.
//
// Die Plattformen melden unterschiedlich: Samsung gibt eine nackte Zahl,
// LG ein Objekt (getUpdateProgress bzw. das DOM-Event). Was sich nicht deuten
// laesst, wird zu null - der Admin zeigt dann "laeuft" ohne Balken, statt eine
// erfundene Zahl anzuzeigen.
function readProgressPercent(data) {
  if (typeof data === 'number' && isFinite(data)) return Math.max(0, Math.min(100, Math.round(data)));
  if (data && typeof data === 'object') {
    const raw = data.progress !== undefined ? data.progress : data.percent;
    const n = Number(raw);
    if (isFinite(n)) return Math.max(0, Math.min(100, Math.round(n)));
  }
  const n = Number(data);
  return isFinite(n) ? Math.max(0, Math.min(100, Math.round(n))) : null;
}

// Merker "der Gast hat eine OTT-App benutzt". Bewusst in localStorage und nicht
// nur im Vuex-State: laedt die App zwischendurch neu (Portal-Reload, Crash),
// waere der State weg und die Anmeldedaten wuerden beim Ausschalten nicht mehr
// geloescht. Der Merker faellt erst, wenn das Loeschen bestaetigt ist.
const CAST_SESSION_KEY = 'cast_session_active';
function readSessionFlag() {
  try {
    return LocalStorage.get(CAST_SESSION_KEY) === '1';
  } catch (e) {
    return false;
  }
}
function writeSessionFlag(active) {
  try {
    if (active) LocalStorage.save(CAST_SESSION_KEY, '1');
    else LocalStorage.remove(CAST_SESSION_KEY);
  } catch (e) {
    /* Storage nicht verfuegbar - dann bleibt es beim Vuex-State */
  }
}

// Merker "der SoftAP wurde fuer einen Gast konfiguriert". Getrennt von
// sessionActive, weil ein Gast sich mit dem SoftAP verbinden kann, OHNE eine
// OTT-App zu starten - sein Zugang muss beim Gastwechsel trotzdem ungueltig
// werden. Ebenfalls persistiert, damit ein App-Reload ihn nicht verliert.
const SOFTAP_ACTIVE_KEY = 'cast_softap_active';
function readSoftApFlag() {
  try {
    return LocalStorage.get(SOFTAP_ACTIVE_KEY) === '1';
  } catch (e) {
    return false;
  }
}
function writeSoftApFlag(active) {
  try {
    if (active) LocalStorage.save(SOFTAP_ACTIVE_KEY, '1');
    else LocalStorage.remove(SOFTAP_ACTIVE_KEY);
  } catch (e) {
    /* Storage nicht verfuegbar - dann bleibt es beim Vuex-State */
  }
}

// Merker "es steht Aufraeumarbeit aus, weil der TV in den Standby gegangen ist".
//
// Er unterscheidet das Aufwachen aus dem Standby von der Rueckkehr aus einer
// Fremd-App: beides sieht fuer die App gleich aus (visibilitychange), aber nur
// im ersten Fall darf geloescht werden - sonst waere der Gast direkt nach dem
// Start von YouTube wieder abgemeldet und sein Handy aus dem SoftAP geworfen.
//
// Persistiert, weil er einen echten Kaltstart ueberleben muss: mit Samsung
// "Virtual Standby Off" bzw. LG "Instant On Boot" bootet der TV beim
// Einschalten neu, und dann ist dieser Merker die einzige Spur, dass noch
// aufzuraeumen ist. Er faellt erst, wenn NICHTS mehr aussteht.
// Merker "der letzte Neustart diente dem Loeschen der Anmeldedaten".
//
// Auf Firmwares ohne deleteAppPrivateInfoNow ist der Neustart die einzige Art zu
// loeschen - und es gibt keine Rueckmeldung darueber. Ohne diesen Merker koennte
// sessionActive nie fallen, und eine einzige Cast-Nutzung wuerde den TV nach
// jedem Standby neu starten. Gesetzt unmittelbar vor dem Neustart, ausgewertet
// beim naechsten App-Start.
// Ein Schluessel, der auf dem Geraet steht, aber noch NICHT gilt.
//
// Auf Geraeten, die ihren Hotspot nicht neu starten koennen, wird ein neuer
// Schluessel erst mit einem Neustart des TV wirksam. Er wird deshalb kurz vor
// einem Neustart geschrieben und hier gemerkt - angezeigt wird bis dahin
// weiter der alte, denn genau der wird noch gesendet. Nach dem Neustart
// befoerdert settleAfterRebootClear ihn zum gueltigen Schluessel.
const SOFTAP_KEY_STAGED = 'cast_softap_key_staged';
function readStagedKey() {
  try {
    return LocalStorage.get(SOFTAP_KEY_STAGED) || null;
  } catch (e) {
    return null;
  }
}
function writeStagedKey(key) {
  try {
    if (key) LocalStorage.save(SOFTAP_KEY_STAGED, key);
    else LocalStorage.remove(SOFTAP_KEY_STAGED);
  } catch (e) {
    /* Storage nicht verfuegbar - dann entfaellt der Wechsel */
  }
}

const REBOOT_CLEAR_KEY = 'cast_reboot_clear';
// Cast wurde auf diesem Geraet schon vorbereitet (Token + ToS). main.js liest
// denselben Schluessel, um die ToS nach einem Neustart vorzuziehen.
export const CAST_TOS_KEY = 'cast_tos_prepared';
// Merker "Neustart steht unmittelbar bevor" mit Zeitstempel.
//
// Am U800F (19.10.2026) startete BringToFront.forReboot die tv-app NEU, statt
// sie nur nach vorn zu holen: zwei Sekunden nach "launch ok" meldete sie sich
// frisch am Socket an. Der Timer, der danach rebootDevice rufen sollte, starb
// mit der alten Seite - der TV startete nie neu, die Anmeldedaten blieben, und
// beim Einschalten holte die Firmware wieder YouTube nach vorn.
//
// Der neu gestartete Code findet den Merker beim Start (settleAfterRebootClear)
// und loest den Neustart selbst aus. Juenger als REBOOT_RESUME_MS heisst: die
// App wurde gerade eben vom eigenen launch neu gestartet. Aelter heisst: der
// Neustart ist gelaufen (ein TV braucht dafuer deutlich laenger), der Merker
// ist nur liegengeblieben.
const REBOOT_PENDING_KEY = 'cast_reboot_pending_at';
const REBOOT_RESUME_MS = 30000;
// Pause nach dem App-Start, bevor der nachgeholte Neustart ausgeloest wird.
//
// Zwei Gruende: webapis wird in index.html nachgeladen und ist beim Start
// noch nicht da - und die Firmware muss die frisch gestartete tv-app als
// "zuletzt benutzte App" uebernommen haben, bevor der TV neu startet. Mit
// 3 s (19.10.2026) lief der Neustart, beim Einschalten holte der U800F aber
// trotzdem die interne Samsung-App (Cast/YouTube) nach vorn. Am 30.09., als
// der launch die App noch nicht neu startete, lagen zwischen launch und
// Neustart ebenfalls nur 2 s - der Unterschied ist der zusaetzliche
// App-Start. VERSUCH mit mehr Zeit.
const REBOOT_RESUME_DELAY_MS = 15000;
function writeRebootPending(on) {
  try {
    if (on) LocalStorage.save(REBOOT_PENDING_KEY, String(Date.now()));
    else LocalStorage.remove(REBOOT_PENDING_KEY);
  } catch (e) {
    /* ohne Storage bleibt es beim Timer */
  }
}
function readRebootPendingAge() {
  try {
    const at = parseInt(LocalStorage.get(REBOOT_PENDING_KEY), 10);
    return isNaN(at) ? null : Date.now() - at;
  } catch (e) {
    return null;
  }
}

function readRebootClearFlag() {
  try {
    return LocalStorage.get(REBOOT_CLEAR_KEY) === '1';
  } catch (e) {
    return false;
  }
}
function writeRebootClearFlag(pending) {
  try {
    if (pending) LocalStorage.save(REBOOT_CLEAR_KEY, '1');
    else LocalStorage.remove(REBOOT_CLEAR_KEY);
  } catch (e) {
    /* Storage nicht verfuegbar - dann bleibt es beim Vuex-State */
  }
}

// Merker "auf diesem TV wurde ein Checkout-Snapshot genommen".
//
// Warum ueberhaupt ein Merker: HCAP kann NICHT abgefragt werden, ob ein
// Snapshot existiert. Ohne ihn liesse sich nie sagen, ob requestCheckout etwas
// zurueckzusetzen hat - und genau daran ist das Loeschen im Feld gescheitert
// (siehe Cast/LG.js clearCredentialsNow).
//
// Persistiert, weil der Snapshot auf dem GERAET liegt und einen App-Reload,
// einen Neustart und einen Stromausfall ueberdauert.
const CHECKOUT_SNAPSHOT_KEY = 'cast_checkout_snapshot';
function readSnapshotFlag() {
  try {
    return LocalStorage.get(CHECKOUT_SNAPSHOT_KEY) === '1';
  } catch (e) {
    return false;
  }
}
function writeSnapshotFlag(taken) {
  try {
    if (taken) LocalStorage.save(CHECKOUT_SNAPSHOT_KEY, '1');
    else LocalStorage.remove(CHECKOUT_SNAPSHOT_KEY);
  } catch (e) {
    /* Storage nicht verfuegbar - dann gilt der Snapshot als nicht genommen */
  }
}

// Merker "fuer diesen Gastwechsel wurde das SoftAP-Passwort schon erneuert".
//
// Persistiert wie die uebrigen: ein App-Reload zwischen Rotation und
// abgeschlossenem Aufraeumen wuerde den Merker sonst verlieren, und der naechste
// Besuch der Cast-Seite rotierte erneut - genau der Fehler, den er verhindert.
// Zurueckgesetzt wird er, sobald ein neuer Gastwechsel beginnt (setClearPending).
const SOFTAP_ROTATED_KEY = 'cast_softap_rotated';
function readRotatedFlag() {
  try {
    return LocalStorage.get(SOFTAP_ROTATED_KEY) === '1';
  } catch (e) {
    return false;
  }
}
function writeRotatedFlag(done) {
  try {
    if (done) LocalStorage.save(SOFTAP_ROTATED_KEY, '1');
    else LocalStorage.remove(SOFTAP_ROTATED_KEY);
  } catch (e) {
    /* Storage nicht verfuegbar - dann gilt die Rotation als offen */
  }
}

const CLEAR_PENDING_KEY = 'cast_clear_pending';
function readPendingFlag() {
  try {
    return LocalStorage.get(CLEAR_PENDING_KEY) === '1';
  } catch (e) {
    return false;
  }
}
function writePendingFlag(pending) {
  try {
    if (pending) LocalStorage.save(CLEAR_PENDING_KEY, '1');
    else LocalStorage.remove(CLEAR_PENDING_KEY);
  } catch (e) {
    /* Storage nicht verfuegbar - dann bleibt es beim Vuex-State */
  }
}

// Ein Lauf pro Power-Zyklus.
//
// Auf EINEN Druck der Power-Taste reagieren drei unabhaengige Stellen:
// device/turnOff, der remotepower-PowerStateListener und der PowerStateWrapper
// (visibilitychange). Keine kennt die anderen, und sessionActive faellt erst
// nach dem Resolve - ohne diese Merker liefe die gesamte Aufraeumarbeit also
// dreifach. Auf LG waren das dreimal vier hcap-Aufrufe mit je 5 s Timeout.
// Ausgangswerte der Client-Abfragen, gemessen bevor sich ein Handy verbinden
// konnte. Siehe watchSoftAP.
let softApBaseline = {};

// Der Listener, der die verbundenen GERAETE meldet
// (webapis.network.addSoftAPClientInfoListener). Getrennt von softApListener,
// der am Zustand des Hotspots selbst haengt - beide koennen nebeneinander
// laufen und muessen einzeln abgemeldet werden.
let softApClientListener = null;

// Hat einer der SoftAP-Listener in diesem App-Lauf schon einmal gemeldet?
//
// Erst dann taugt pairing.connected als Nachweis. Am U800F (Tizen 9.0) sind
// beide Listener registrierbar und trotzdem stumm - dort hiesse "nicht
// verbunden" nur "keine Auskunft", und eine Cast-Nutzung vom Handy bliebe
// unsichtbar. Siehe prepareForStandby.
let softApEventSeen = false;


let standbyRun = null;
// Der Teil, auf den das TOR wartet: das Loeschen der Anmeldedaten. Bewusst
// getrennt vom vollen Lauf - das neue SoftAP-Passwort darf einen App-Start nicht
// aufhalten (enableSoftAP braucht auf manchen Firmwares zehn Sekunden pro
// Versuch, gemessene 17 s standen vor dem Netflix-Start).
let clearRun = null;
// Der vollstaendige Lauf inklusive SoftAP. Nur zum Entdoppeln - niemand wartet
// darauf.
let fullRun = null;

// Zeitbudget fuer den Ausschaltpfad. Was bis dahin nicht fertig ist, laeuft im
// Hintergrund weiter - nur wartet niemand mehr darauf, und der Bildschirm wird
// dunkel. Liegengebliebenes holt der Wake-Pfad nach; die Merker stehen ja noch.
const STANDBY_BUDGET_MS = 600;

// Wartezeit zwischen dem Schreiben der Merker und dem Neustart. Siehe
// scheduleStandbyReboot - localStorage bestaetigt sofort, garantiert aber
// nicht, dass der Wert schon dauerhaft liegt.
const REBOOT_FLUSH_MS = 500;
// Versuch (Schalter CAST_REBOOT_FRONT, nur Staging an): die tv-app vor dem
// Standby-Neustart nach vorn holen, damit sie nach dem Hochfahren die
// "zuletzt benutzte App" ist - siehe BringToFront.forReboot. Die Wartezeit
// gibt dem launch Zeit, bevor rebootDevice den TV herunterfaehrt.
const REBOOT_FRONT = String(process.env.CAST_REBOOT_FRONT) === '1';
const REBOOT_FRONT_SETTLE_MS = 2000;

// Nach dieser Zeit im Standby wird der TV neu gestartet.
//
// Der Anlass: auf Firmwares ohne deleteAppPrivateInfoNow (Samsung U800F,
// Tizen 9.0) laesst sich die OTT-Anmeldung des Gastes NICHT sofort loeschen.
// Es bleibt deleteAppPrivateInfo("ON"), und das wirkt erst beim naechsten
// Neustart - im Virtual Standby gibt es aber keinen. Ohne PMS weiss die App
// zudem nicht, wann der Gast abreist.
//
// Eine Stunde Standby ist der praktikable Ersatz fuer dieses Wissen: zwischen
// zwei Gaesten steht der TV immer laenger als eine Stunde still, und wer nur
// kurz ausschaltet, behaelt seine Anmeldung. Der Neustart faellt in eine Zeit,
// in der niemand hinsieht.
//
// Seit 09/2026 in Produktion EINGESCHALTET (CAST_STANDBY_REBOOT_MIN = 60) -
// trotz des folgenden Befunds, weil es ohne PMS keinen anderen Weg gibt:
//
// An EINEM Samsung-Geraet hat rebootDevice aus dem Standby den TV komplett
// totgelegt - welches Modell, ist nicht festgehalten; ein BU800 hat es danach
// zweimal fehlerfrei ueberstanden: keine Socket-Verbindung mehr, Power-Taste brachte nur
// einen schwarzen Bildschirm, erst ein weiteres Power Off / On holte die App
// zurueck. Im Log stand nur '[systemcontrol.js] rebootDevice message data
// null'. Tritt das wieder auf: CAST_STANDBY_REBOOT_MIN auf 0 und das Modell
// festhalten - oder den Neustart auf den Wake-Zeitpunkt verlegen, wo ein
// Neustart der getestete Normalfall ist.
const STANDBY_REBOOT_MIN = (function readRebootMinutes() {
  const raw = parseInt(String(process.env.CAST_STANDBY_REBOOT_MIN), 10);
  return isNaN(raw) || raw < 0 ? 0 : raw;
})();
let standbyRebootTimer = null;

// Ist der TV gerade an? Unmittelbar vor rebootDevice gefragt.
//
// Gemessen am U800F (21.10.2026): POWER ein fiel in die 15 s vor dem
// nachgeholten Neustart, der Neustart lief trotzdem - danach schwarzer
// Bildschirm, erst HOME startete die App. document.hidden hilft dort nicht:
// die per launch im Standby nach vorn geholte App gilt als sichtbar.
// remotepower.getPowerState antwortet synchron. Nur ein eindeutiges NORMAL
// verhindert den Neustart; keine oder eine unklare Antwort aendert nichts.
// Roher Zustand ('NORMAL', 'STANDBY', ...) oder '' ohne Antwort.
function readPowerState() {
  try {
    const power =
      (window.b2bapis && window.b2bapis.b2bpower) ||
      (window.webapis && window.webapis.remotepower);
    if (!power || typeof power.getPowerState !== 'function') return '';
    return String(power.getPowerState() || '').toUpperCase();
  } catch (e) {
    return '';
  }
}

function tvIsOn() {
  const state = readPowerState();
  console.log('[cast] Power-Zustand vor dem Neustart: ' + (state || '-'));
  return state.indexOf('NORMAL') !== -1;
}

// Nachweislich im Standby? Nur dann darf ein Aufruf von clearGuestCredentials
// den geplanten Neustart stehen lassen (siehe dort).
function tvIsInStandby() {
  const state = readPowerState();
  return state.indexOf('STANDBY') !== -1 || state.indexOf('OFF') !== -1;
}

// Ab hier gehoert das Ende der Seite zum Neustart: der launch vor dem
// Neustart (BringToFront.forReboot startet die App neu, der neue Code holt den
// Neustart ueber REBOOT_PENDING_KEY nach) oder rebootDevice selbst.
let rebootCommitted = false;

// Laeuft gerade ein geplanter Standby-Neustart? Fuer get_info.
export function standbyRebootPlanned() {
  return !!standbyRebootTimer;
}

// Endet die Seite, waehrend ein Standby-Neustart nur GEPLANT ist (Neuladen per
// SocketAdmin, BuildCheck, Absturz), stirbt der Timer mit ihr - der Merker
// "Neustart zum Loeschen" bliebe aber stehen, und settleAfterRebootClear hielte
// den Neustart beim naechsten Start fuer gelaufen: sessionActive fiele, obwohl
// nichts geloescht wurde. Deshalb den Merker hier zuruecknehmen; sessionActive
// bleibt, der naechste Standby plant neu.
function dropPlannedRebootOnUnload() {
  if (!standbyRebootTimer || rebootCommitted) return;
  writeRebootClearFlag(false);
  console.log('[cast] Seite endet vor dem geplanten Neustart - Merker zurueckgenommen, naechster Standby plant neu');
}
if (typeof window !== 'undefined' && window.addEventListener) {
  window.addEventListener('pagehide', dropPlannedRebootOnUnload);
  window.addEventListener('beforeunload', dropPlannedRebootOnUnload);
}

function cancelStandbyReboot(reason) {
  if (!standbyRebootTimer) return;
  clearTimeout(standbyRebootTimer);
  standbyRebootTimer = null;
  rebootCommitted = false;
  // Der Merker steht seit dem Planen (siehe scheduleStandbyReboot) - ohne
  // Neustart darf er nicht stehen bleiben, sonst gaelte der naechste
  // App-Start als "Neustart zum Loeschen gelaufen".
  writeRebootClearFlag(false);
  console.log('[cast] Standby-Neustart abgebrochen (' + reason + ')');
}


// Wartet hoechstens ms auf ein Promise. Bricht ausdruecklich nur die WARTEZEIT
// ab, nicht die Arbeit: ein halb abgebrochener Geraete-Aufruf waere schlimmer
// als ein spaeter fertiger.
function withBudget(promise, ms, label) {
  return new Promise((resolve) => {
    let done = false;
    const finish = (value) => {
      if (done) return;
      done = true;
      resolve(value);
    };
    const timer = setTimeout(() => {
      console.log('[cast] ' + label + ': Zeitbudget ' + ms + ' ms ausgeschoepft - Rest beim Aufwachen');
      finish(false);
    }, ms);
    Promise.resolve(promise).then(
      (r) => {
        clearTimeout(timer);
        finish(r);
      },
      (e) => {
        clearTimeout(timer);
        console.log('[cast] ' + label + ' fehlgeschlagen:', (e && e.message) || e);
        finish(false);
      }
    );
  });
}

// Hat UNSERE App den Wechsel in den Hintergrund ausgeloest (Start einer
// OTT-App)? Dann ist es kein Standby.
//
// Auf LG ist das die einzige Unterscheidung, die zur Verfuegung steht: einen
// PowerStateListener gibt es dort nicht (siehe keyHandler/LG.js) und
// hcap.power.getPowerMode antwortet in der Ausschaltsequenz nicht mehr. Auf
// Samsung entscheidet stattdessen remotepower.getPowerState - der Merker wird
// dort nicht gebraucht und deshalb auch nicht abgefragt.
let appHandoff = false;

// Der Passwortwechsel ist schon im Standby passiert - die Cast-Seite muss ihn
// nicht nachholen.
//
// Laufzeitmerker, bewusst nicht persistiert: er gilt fuer genau den Weg
// "aufgewacht -> Cast-Seite geoeffnet". Ein App-Neustart dazwischen heisst,
// dass der TV ohnehin durchgestartet ist; dann greift wieder der alte Weg.
let softApRotatedInStandby = false;

// Vuex-Modul fuer die Cast-/OTT-Seite.
//
// Haelt die anzuzeigenden OTT-Apps (Netflix, YouTube, Prime Video, Disney+,
// Paramount+), die SoftAP-Zugangsdaten (fuer den WLAN-QR) sowie den Status von
// Firmware-Update und Settings-Cloning. Die konkreten TV-APIs kapselt der
// Cast-Helper (Samsung Tizen primaer, LG als Platzhalter).

// Ermittelt aus den Device-Daten, welche OTT-Apps aktiv sind. Unterstuetzt:
//   - Array von Keys: ["netflix","youtube"]
//   - Array von Objekten (voll konfiguriert, ueberschreibt Defaults teilweise)
// Faellt auf alle Default-Apps zurueck.
function resolveApps(deviceApps) {
  if (!Array.isArray(deviceApps) || deviceApps.length === 0) {
    return DEFAULT_CAST_APPS.slice();
  }
  const byKey = {};
  CAST_APP_CATALOG.forEach((a) => {
    byKey[a.key] = a;
  });
  const result = [];
  const seen = {};
  deviceApps.forEach((entry) => {
    // Schluessel wie im Katalog: klein, ohne Leerraum - die Liste kann von
    // Hand gepflegt sein (Menue-Parameter, Building-Tabelle).
    const key = String(typeof entry === 'string' ? entry : (entry && entry.key) || '')
      .trim()
      .toLowerCase();
    // Die generische Cast-Kachel gibt es nicht mehr als Kachel - sie startete
    // nichts. Aeltere Geraetekonfigurationen fuehren sie noch.
    if (!key || key === CAST_TILE.key || seen[key]) return;
    if (typeof entry === 'string') {
      if (!byKey[key]) {
        console.log('[cast] Unbekannte App in der Kachelliste uebergangen: ' + entry);
        return;
      }
      result.push(Object.assign({}, byKey[key]));
    } else {
      result.push(Object.assign({}, byKey[key] || {}, entry, { key: key }));
    }
    seen[key] = true;
  });
  return result.length ? result : DEFAULT_CAST_APPS.slice();
}

// Welche Kachelliste gilt? Rangfolge siehe helpers/Cast/buildingApps.js:
// Geraet vor Menue-Parameter vor Building-Tabelle vor Standard.
function pickCastApps(rootState, menuApps) {
  const device = (rootState && rootState.device) || {};
  const nonEmpty = (list) => Array.isArray(list) && list.length > 0;
  const buildingId = (rootState && rootState.buildingId) || device.buildingId;
  let source = 'Standard';
  let list = null;
  if (nonEmpty(device.castApps)) {
    source = 'Geraet (device.castApps)';
    list = device.castApps;
  } else if (nonEmpty(menuApps)) {
    source = 'Menuepunkt (?apps=)';
    list = menuApps;
  } else if (castAppsForBuilding(buildingId)) {
    source = 'Building ' + buildingId + ' (Tabelle)';
    list = castAppsForBuilding(buildingId);
  }
  const apps = resolveApps(list);
  console.log('[cast] Kachelliste aus ' + source + ': ' + apps.map((a) => a.key).join(','));
  return apps;
}

// Individuelle Geraetekennung: Raumname + kurzer Zufall, z.B. "Zimmer101-AB3C".
//
// Sie ist zweierlei zugleich: die SSID des eigenen SoftAP UND - auf LG - der
// Geraetename (Property tv_name), unter dem der TV in Cast-/AirPlay-Listen
// erscheint. Ohne sie heissen baugleiche Geraete in Nachbarzimmern alle gleich
// und der Gast kann seinen TV nicht auseinanderhalten.
//
// Ueber Neustarts hinweg stabil (localStorage), damit der Name nicht bei jedem
// Start wechselt und nicht bei jedem Start auf den TV geschrieben werden muss.
// Neu vergeben wird er nur beim Gastwechsel (rotateSoftAP).
const SOFTAP_SSID_KEY = 'cast_softap_ssid';
let sessionSoftApSsid = (function readSsid() {
  try {
    return LocalStorage.get(SOFTAP_SSID_KEY) || null;
  } catch (e) {
    return null;
  }
})();
function rememberSsid(ssid) {
  sessionSoftApSsid = ssid || null;
  try {
    if (ssid) LocalStorage.save(SOFTAP_SSID_KEY, ssid);
    else LocalStorage.remove(SOFTAP_SSID_KEY);
  } catch (e) {
    /* Storage nicht verfuegbar - dann gilt der Wert nur fuer diese Sitzung */
  }
}
// Das SoftAP-Passwort laesst sich auf BEIDEN Plattformen nur SETZEN, nicht
// lesen: LG gibt soft_ap_password prinzipbedingt nicht heraus, und auf Samsung
// gibt es keinen dokumentierten Getter. Wir muessen es deshalb selbst erzeugen,
// selbst merken und beim Anzeigen aus unserem Merker nehmen.
//
// Persistiert wie die Kennung, und aus demselben Grund: ein nur zur Laufzeit
// gemerktes Passwort waere nach jedem Neustart weg, die App wuerde ein neues
// schreiben, und das Handy des Gastes flaege aus dem Netz. Neu vergeben wird es
// ausschliesslich beim Gastwechsel (rotateSoftAP).
const SOFTAP_KEY_KEY = 'cast_softap_key';
let sessionSoftApKey = (function readKey() {
  try {
    return LocalStorage.get(SOFTAP_KEY_KEY) || null;
  } catch (e) {
    return null;
  }
})();
function rememberKey(key) {
  sessionSoftApKey = key || null;
  try {
    if (key) LocalStorage.save(SOFTAP_KEY_KEY, key);
    else LocalStorage.remove(SOFTAP_KEY_KEY);
  } catch (e) {
    /* Storage nicht verfuegbar - dann gilt der Wert nur fuer diese Sitzung */
  }
}
function buildSoftApPassword() {
  // Ohne verwechselbare Zeichen (0/O, 1/l/I) - der Gast tippt es im Zweifel ab.
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
  let s = '';
  for (let i = 0; i < 8; i++) s += chars.charAt(Math.floor(Math.random() * chars.length));
  return s;
}

function buildRoomSsid(room) {
  // SSID-Basis = Raumname (nur Alnum, Laenge begrenzt) + 4-stelliger Zufall
  // ohne verwechselbare Zeichen. Nur wenn wirklich kein Raumname vorliegt,
  // greift 'HotelTV' als Notnagel.
  //
  // Vorgabe: einheitlich [max. 24 Zeichen]-[4 Zeichen], z.B. Zi399-T8UE.
  //
  // Ein '[TV]'-Praefix, wie es ein BU800 dem gesendeten Namen voranstellt,
  // stammt NICHT von hier - siehe die Anmerkung an setSoftAPSSID in
  // helpers/Cast/Tizen.js. Bewusst wird der Raumname deshalb nicht gekuerzt, um
  // Platz dafuer zu lassen: die Kennung soll auf allen Geraeten gleich aufgebaut
  // sein. Rest-Risiko dieser Entscheidung: 24 + '-' + 4 = 29 Zeichen, mit einem
  // vierstelligen Praefix 33 - und eine SSID darf nur 32 Bytes lang sein. Das
  // trifft erst Raumnamen ab 20 Zeichen und nur auf praefigierenden Firmwares.
  const base = String(room || '').replace(/[^A-Za-z0-9]+/g, '').slice(0, 24) || 'HotelTV';
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let rnd = '';
  for (let i = 0; i < 4; i++) rnd += chars.charAt(Math.floor(Math.random() * chars.length));
  return base + '-' + rnd;
}

// Registrierter SoftAP-State-Listener (nur einer gleichzeitig). Modulweit und
// nicht im State, weil eine Funktionsreferenz nichts in Vuex zu suchen hat.
let softApListener = null;

// Aktuell am SoftAP verbundene Geraete, MAC -> Anzeigename. Der TV meldet pro
// Geraet ein eigenes Ereignis mit State 1/0; nur so laesst sich sagen, ob nach
// einem "getrennt" noch jemand anders dranhaengt.
let softApClients = {};

// Wiederholte Abfrage des Ist-Zustands, wenn das Geraet eine hergibt. Zweite
// Quelle neben dem Listener, der nur Aenderungen meldet - siehe watchSoftAP.
let softApPoll = null;
const SOFTAP_POLL_MS = 3000;

// Lesbarer Text aus einem Fehler. HCAP-Fehlerobjekte tragen errorMessage
// (nicht message) - ohne das landet in der Notification nur "[object Object]".
function errText(err) {
  if (!err) return 'unbekannter Fehler';
  if (typeof err === 'string') return err;
  return String(err.errorMessage || err.message || err.name || JSON.stringify(err));
}

// Liefert die App-Id fuer Plattform UND aktive Sprache. Fuer manche Apps gibt es
// je Geraetesprache eine eigene Id (vor allem auf Samsung) - resolveAppId
// beruecksichtigt das. Der plattformfremde Fallback bleibt erhalten, damit sich
// das Verhalten im Browser/Preview nicht aendert.
function appIdFor(app, lang) {
  if (!app) return '';
  const resolved = resolveAppId(app, Cast.platform, lang);
  if (resolved) return resolved;
  if (Cast.platform === 'lg') return app.tizenAppId || '';
  return app.lgAppId || '';
}

export default {
  namespaced: true,
  state: () => ({
    apps: DEFAULT_CAST_APPS.slice(),
    selectedIndex: 0,
    token: DEFAULT_CAST_TOKEN,
    prepared: false,
    // Pairing-Strategie ('softap-qr' | 'lg-native-cast' | 'network-qr' | 'screenshare').
    // Hier bewusst null: Vuex ruft diese Funktion waehrend der Store-Erzeugung
    // auf, und da ist der Import-Zyklus store -> cast -> Cast -> DeviceType ->
    // Device -> Device/LG -> store noch nicht aufgeloest. Ein Cast-Aufruf an
    // dieser Stelle laeuft in "Cannot read properties of undefined".
    // Gesetzt wird die Strategie in init()/detect().
    strategy: null,
    // Ergebnis der Geraete-Erkennung (nur LG; null bis detect() durch ist)
    capabilities: null,
    // SoftAP bzw. verbundenes WLAN -> Grundlage des QR
    softAP: { ssid: '', key: '', security: 'WPA', hidden: false },
    // Verbindungsstatus am SoftAP. watching=false heisst "der TV meldet
    // Verbindungen nicht" (LG) - dann darf die Seite auch kein "warte auf
    // Verbindung" zeigen, das nie umschlagen wuerde.
    // watching  = dieses Geraet MELDET Verbindungen nachweislich, naemlich weil
    //             schon ein Ereignis gekommen ist. Nur dann zeigt die Cast-Seite
    //             eine Verbindungszeile - sonst stuende dort ein "warte auf
    //             Verbindung", das nie umschlaegt (Tizen 9.0: der Client-Listener
    //             wird angenommen und feuert nie).
    // listening = die Listener SIND registriert. Nur fuer die Diagnose auf
    //             /info; ohne die Trennung sah ein stummes Geraet genauso aus
    //             wie ein Geraet ohne Listener.
    pairing: { watching: false, listening: false, connected: false, clients: 0, name: '', raw: '' },
    // Ergebnis des SSID-Rueckvergleichs nach dem Schreiben (nur LG kann das).
    // null = noch nicht geprueft. readable=false heisst "keine Aussage
    // moeglich", nicht "fehlgeschlagen".
    softApVerify: null,
    // Status von Firmware-Update / Cloning (fuer Socket-Feedback / UI)
    firmware: { active: false, progress: null, error: null },
    clone: { active: false, progress: null, error: null },
    // Der Gast hat mindestens eine OTT-App gestartet - beim Ausschalten sind
    // dann Anmeldedaten auf dem TV zu erwarten, die geloescht werden muessen.
    sessionActive: readSessionFlag(),
    // Der SoftAP wurde fuer einen Gast konfiguriert - beim Gastwechsel muss
    // sein Passwort neu vergeben werden.
    softApActive: readSoftApFlag(),
    // Der TV war im Standby und das Aufraeumen ist noch nicht durch. Nur wenn
    // das steht, darf der Wake-Pfad loeschen.
    clearPending: readPendingFlag(),
    // Fuer DIESEN Gastwechsel ist das SoftAP-Passwort schon erneuert. Verhindert
    // die Rotation bei jedem Oeffnen der Cast-Seite - siehe settleClearPending.
    softApRotated: readRotatedFlag(),
    // Stand des Checkout-Snapshots - und, wenn er fehlt, WARUM.
    //
    // Gehoert in den State und nicht nur ins Log: die Entscheidung faellt bei
    // cast/init, also beim Oeffnen der Cast-Seite. Wer danach den Live-Log
    // aufmacht, sieht davon nichts mehr - genau so ist es beim ersten Versuch
    // am Geraet passiert. Ueber die Zustandsmeldung und die Info-Seite laesst
    // sich der Stand dagegen jederzeit nachlesen.
    snapshotTaken: readSnapshotFlag(),
    snapshotReason: '',
  }),
  mutations: {
    setApps(state, apps) {
      state.apps = apps;
    },
    setSelectedIndex(state, index) {
      state.selectedIndex = index;
    },
    setToken(state, token) {
      if (token) state.token = token;
    },
    setPrepared(state, value) {
      state.prepared = value;
    },
    setStrategy(state, strategy) {
      state.strategy = strategy;
    },
    setCapabilities(state, caps) {
      state.capabilities = caps;
    },
    setSoftAP(state, info) {
      state.softAP = Object.assign({}, state.softAP, info);
    },
    setSoftApVerify(state, info) {
      state.softApVerify = info;
    },
    setPairing(state, info) {
      state.pairing = Object.assign({}, state.pairing, info);
    },
    setFirmware(state, info) {
      state.firmware = Object.assign({}, state.firmware, info);
    },
    setClone(state, info) {
      state.clone = Object.assign({}, state.clone, info);
    },
    setSoftApActive(state, active) {
      state.softApActive = !!active;
      writeSoftApFlag(!!active);
    },
    setSessionActive(state, active) {
      state.sessionActive = !!active;
      writeSessionFlag(!!active);
    },
    setClearPending(state, pending) {
      state.clearPending = !!pending;
      writePendingFlag(!!pending);
      // Ein NEUER Gastwechsel - die Rotation steht wieder aus.
      if (pending) {
        state.softApRotated = false;
        writeRotatedFlag(false);
      }
    },
    setSoftApRotated(state, done) {
      state.softApRotated = !!done;
      writeRotatedFlag(!!done);
    },
    setSnapshot(state, info) {
      state.snapshotTaken = !!(info && info.taken);
      state.snapshotReason = (info && info.reason) || '';
    },
  },
  actions: {
    // Baut Apps-Liste + Token aus den Device-Daten auf und bereitet (auf
    // unterstuetzten Geraeten) Google Cast fuer den Check-in vor.
    //
    // options.apps: Kachelliste vom Menuepunkt ('#cast?apps=...'), siehe
    // pickCastApps fuer die Rangfolge.
    init({ commit, dispatch, rootState }, options) {
      const device = (rootState && rootState.device) || {};
      commit('setApps', pickCastApps(rootState, options && options.apps));
      commit('setToken', device.castToken || DEFAULT_CAST_TOKEN);
      commit('setSelectedIndex', 0);
      // Dem Helfer sagen, ob auf diesem TV schon ein Snapshot liegt - HCAP
      // kennt keine Abfrage dafuer, der Merker ist die einzige Quelle.
      Cast.noteCheckoutSnapshot(readSnapshotFlag());
      // Ab jetzt mitschreiben, wenn soft_ap von selbst auf 0 faellt. Das ist
      // auf Geraeten ohne Power-Listener und ohne brauchbare Sonde der einzige
      // Nachweis, dass der TV zwischendurch aus war - und davon haengt der
      // Gastwechsel ab (neues SoftAP-Passwort, Loeschen der Anmeldedaten).
      //
      // Der Melder vergibt gleich das neue Passwort, solange der TV aus ist -
      // siehe rotateSoftApInStandby.
      Cast.watchSoftApDrop(() => dispatch('rotateSoftApInStandby').catch(() => undefined));
      // Strategie sofort (versionsbasiert) setzen, damit die Seite sofort
      // bedienbar ist; detect() verfeinert sie anschliessend anhand der
      // tatsaechlichen Geraete-Faehigkeiten.
      commit('setStrategy', Cast.castStrategy());
      dispatch('prepare');
      // Geraetenamen erst nach detect() setzen: vorher ist der aktuelle Wert
      // nicht bekannt und wir wuerden bei jedem Start blind schreiben.
      //
      // Und ABGEWARTET, nicht nebenher: auf Firmwares, die tv_name nur
      // schreiben, aber nicht lesen lassen (43UK660H, webOS 25), ist dieser
      // Schreibzugriff der einzige Nachweis, dass ein eigener SoftAP-QR
      // ueberhaupt baubar ist - und die Strategie haengt daran. Liefe er
      // nebenher, waere die Verbindungsseite schon gebaut, bevor die Antwort da
      // ist. Es ist genau EIN setProperty (gemessen ~10 ms).
      return dispatch('detect').then((caps) =>
        dispatch('applyDeviceName')
          .catch(() => false)
          .then(() => {
            // Der Schreibversuch kann die Faehigkeiten geaendert haben.
            const fresh = Cast.capabilities();
            if (fresh) commit('setCapabilities', Object.assign({}, fresh));
            commit('setStrategy', Cast.castStrategy());
            // Den Checkout-Snapshot nachholen, falls dieser TV noch keinen hat.
            // Die Aktion entscheidet selbst, ob der Zeitpunkt taugt - sie laeuft
            // nur auf einem Geraet ohne Spur eines Gastes. Ist es dafuer zu
            // spaet, sagt sie das im Log, und der Admin-Befehl bleibt der Weg.
            // Nicht abgewartet: die Verbindungsseite haengt nicht daran.
            dispatch('takeCheckoutSnapshot').catch(() => undefined);
            return caps;
          })
      );
    },
    // Sorgt dafuer, dass der TV eine raumbezogene Kennung traegt - unabhaengig
    // von der Pairing-Strategie.
    //
    // Der Grund: auf LG spannt die eingebaute Cast-App ihren Hotspot selbst auf
    // und benutzt dafuer tv_name. Steht dort der Auslieferungsname, heissen
    // baugleiche Geraete in Nachbarzimmern alle gleich, und der Gast trifft
    // womoeglich den Fernseher nebenan. Deshalb wird der Name auch dann
    // gesetzt, wenn wir gar keinen eigenen QR anzeigen.
    //
    // Geschrieben wird nur bei Abweichung: der Name ist ueber Neustarts stabil,
    // ein Schreibzugriff bei jedem Start waere unnoetig.
    applyDeviceName({ commit, rootState }) {
      const device = (rootState && rootState.device) || {};
      const softApCfg = device.softAP || {};
      if (!sessionSoftApSsid) {
        const regRoom = (Registration.getData && Registration.getData.room) || '';
        rememberSsid(softApCfg.ssid || buildRoomSsid(device.room || regRoom));
      }
      const wanted = sessionSoftApSsid;
      const current = Cast.getDeviceName();
      // deviceNameMatches statt eines direkten Vergleichs: stellt die Firmware
      // dem Namen etwas voran ('[TV]devo-WUN9'), ist das keine Abweichung.
      // Vorher wurde deshalb bei jedem Start erneut geschrieben - und
      // setTVName scheitert auf diesen Geraeten.
      if (!wanted || Cast.deviceNameMatches(wanted)) return Promise.resolve(false);
      return Cast.setDeviceName(wanted).then((ok) => {
        console.log(
          '[cast] Geraetename "' + (current || '-') + '" -> "' + wanted + '": ' + (ok ? 'ok' : 'fehlgeschlagen')
        );
        if (ok) commit('setSoftAP', { ssid: wanted });
        return ok;
      });
    },
    // Geraete-Faehigkeiten ermitteln und daraus die Pairing-Strategie ableiten.
    // Auf Nicht-LG-Geraeten ein No-op mit sofortigem Ergebnis.
    detect({ commit }, force) {
      return Cast.detect(force)
        .then((caps) => {
          // Flache Kopie: der Helper mutiert sein Capability-Objekt in place,
          // eine gleiche Referenz wuerde bei erneutem detect() keine
          // Reaktivitaet ausloesen.
          commit('setCapabilities', caps ? Object.assign({}, caps) : null);
          commit('setStrategy', Cast.castStrategy());
          return caps;
        })
        .catch((e) => {
          console.log('[cast] detect error', e);
          commit('setStrategy', Cast.castStrategy());
          return null;
        });
    },
    // Token init + Google Cast initialize + acceptTermsOfService (idempotent).
    prepare({ commit, state }) {
      if (state.prepared) return Promise.resolve();
      try {
        Cast.prepareForCheckIn(state.token);
        commit('setPrepared', true);
        // Merker fuer den App-Start: nur wo Cast schon einmal vorbereitet
        // wurde, akzeptiert main.js die ToS nach einem Neustart erneut (siehe
        // Cast.acceptCastTermsAtStart). In Haeusern ohne Cast bleibt es so
        // bei "nie angefasst".
        try {
          LocalStorage.save(CAST_TOS_KEY, '1');
        } catch (e) {
          /* ohne Merker nur kein Vorziehen beim Start */
        }
      } catch (e) {
        console.log('[cast] prepare error', e);
      }
      return Promise.resolve();
    },
    incSelected({ commit, state }) {
      const next = state.selectedIndex + 1 >= state.apps.length ? 0 : state.selectedIndex + 1;
      commit('setSelectedIndex', next);
    },
    decSelected({ commit, state }) {
      const prev = state.selectedIndex - 1 < 0 ? state.apps.length - 1 : state.selectedIndex - 1;
      commit('setSelectedIndex', prev);
    },
    // Startet die ausgewaehlte OTT-App ueber die TEP-WAS-API.
    launchSelected({ state, dispatch }) {
      const app = state.apps[state.selectedIndex];
      return dispatch('launchApp', app);
    },
    // Uebergibt an das native Cast-/Pairing-Overlay des TV (LG >= 25).
    launchCastEntry({ commit, dispatch, state }) {
      const wasActive = state.sessionActive;
      // Erst das Tor: solange die Daten des Vorgaengers noch da sind, wird
      // nichts uebergeben. Der Merker wird bewusst DANACH gesetzt - sonst
      // koennte der noch laufende Lauf ihn gleich wieder loeschen.
      return dispatch('clearGate').then(() => {
        // Auch ueber das native Overlay (DIAL/AirPlay) kann sich der Gast in
        // einer TV-App anmelden - gleiche Aufraeumpflicht wie beim App-Start.
        commit('setSessionActive', true);
        dispatch('armClearCredentials');
        appHandoff = true;
        ForeignApp.noteOwnLaunch();
        return Cast.launchCastEntry().catch((err) => {
          // Siehe launchApp: kein Einstieg, keine Anmeldung, kein Merker.
          if (!wasActive) {
            console.log('[cast] Cast-Einstieg fehlgeschlagen - Sitzungsmerker zurueckgenommen');
            commit('setSessionActive', false);
          }
          return Promise.reject(err);
        });
      }).catch((err) => {
        dispatch(
          'notification/error',
          {
            title: 'Cast',
            message: errText(err),
          },
          { root: true }
        );
        return Promise.reject(err);
      });
    },
    // payload: das App-Objekt, oder { app, onProgress, quiet } - onProgress
    // meldet eine Nachinstallation ({ phase: 'start'|'download'|'install',
    // progress }), quiet unterdrueckt die rote Fehlermeldung, weil die
    // aufrufende Seite den Fehlschlag selbst anzeigt (Direktstart in Cast.vue).
    launchApp({ commit, dispatch, state, rootState }, payload) {
      const wrapped = !!(payload && payload.app);
      const app = wrapped ? payload.app : payload;
      const onProgress = wrapped ? payload.onProgress : null;
      const quiet = wrapped && !!payload.quiet;
      const lang = (rootState && rootState.lang && rootState.lang.active) || null;
      // Ohne belegte Id (DAZN, Joyn - bis die clone.xml ausgewertet ist) wird
      // die App ueber ihren Namen unter den installierten Apps des TV gesucht.
      // Findet sich nichts, kann der Start nicht gelingen - dann gar nicht erst
      // die Sitzung vermerken.
      const configuredId = appIdFor(app, lang);
      let appId = configuredId;
      // Mehrere Ids im Katalog: die nehmen, die das Geraet fuehrt (Cast.pickAppId).
      const resolveId = configuredId
        ? Cast.pickAppId(app, lang).then((picked) => picked || configuredId)
        : Cast.findInstalledAppId(app);
      // Stand der Merker schon vorher? Dann darf ein Fehlschlag ihn NICHT
      // zuruecknehmen - eine frueher erfolgreich benutzte App hat ihre
      // Anmeldung ja hinterlassen.
      const wasActive = state.sessionActive;
      // Ab hier ist mit Gast-Anmeldedaten auf dem TV zu rechnen. Zwei Stufen:
      //  1. Merker setzen -> beim naechsten Standby wird aufgeraeumt.
      //  2. Loeschen beim naechsten Reboot scharf schalten - das ueberlebt auch
      //     einen Stromausfall, bei dem die App nichts mehr ausfuehren kann.
      // Das Tor ZUERST: hier - und nicht im Ausschaltpfad - wird das
      // Versprechen eingeloest, dass der naechste Gast nichts vom Vorgaenger
      // vorfindet. Genau deshalb darf das Loeschen in den Wake-Pfad wandern.
      // Und die Reihenfolge ist wichtig: wuerde setSessionActive VOR dem Tor
      // stehen, koennte der noch laufende Lauf den frischen Merker gleich
      // wieder loeschen und der neue Gast bliebe unaufgeraeumt.
      return resolveId.then((found) => {
        appId = found;
        if (!appId) {
          return Promise.reject(
            new Error('keine App-Id und auf dem TV nicht gefunden - App installieren bzw. Id aus der clone.xml eintragen')
          );
        }
        return dispatch('clearGate');
      }).then(() => {
        commit('setSessionActive', true);
        dispatch('armClearCredentials');
        // Der Wechsel in den Hintergrund ist ab jetzt KEIN Standby (LG, siehe
        // appHandoff).
        appHandoff = true;
        ForeignApp.noteOwnLaunch();
        // name/aliases fuer die Ausweichwege: LG sucht die App per Namen in der
        // Preloaded-Liste, Tizen startet sie ueber die Id am Geraet
        // (tizen.application.launch kennt die WAS-Store-Id nicht).
        return Cast.launchApp(appId, {
          checkCapability: true,
          name: app && app.name,
          aliases: app && app.aliases,
          onProgress: onProgress,
        }).catch((err) => {
          // Der Start ist fehlgeschlagen - es kann also keine Anmeldung
          // entstanden sein. Den Merker zuruecknehmen.
          //
          // Ohne das blieb er stehen, obwohl nichts passiert war: am Geraet
          // scheiterte Netflix mit WAS_ERROR_LAUNCH_NOT_EXIST, die Sitzung galt
          // danach aber als aktiv. Auf Firmwares ohne deleteAppPrivateInfoNow
          // faellt sessionActive nie wieder - der TV haette also nach jedem
          // Standby neu gestartet, obwohl nie eine App lief.
          if (!wasActive) {
            console.log('[cast] App-Start fehlgeschlagen - Sitzungsmerker zurueckgenommen');
            commit('setSessionActive', false);
          }
          return Promise.reject(err);
        });
      }).catch((err) => {
        const message = (app && app.name ? app.name + ' (' + (appId || 'ohne Id') + '): ' : '') + errText(err);
        if (quiet) console.log('[cast] Cast/Launch fehlgeschlagen ' + message);
        else dispatch('notification/error', { title: 'Cast/Launch fehlgeschlagen', message: message }, { root: true });
        return Promise.reject(err);
      });
    },
    // Startet eine OTT-App ueber ihren Katalog-Schluessel ('netflix', ...).
    //
    // Fuer die eigene OTT-Kachel im Hauptmenue (ident=netflix, siehe
    // navigation.js): sie fuehrt auf die Cast-Seite, und wenn dort die
    // Verbindung steht, startet diese Aktion genau die eine App - ohne
    // Kachelauswahl davor.
    //
    // Token und App-Liste werden bei Bedarf nachgezogen: gerufen wird sie auch
    // aus dem Socket-Kommando launch_app, also ohne vorheriges init(). Ohne
    // initialisierten SI-Token verweigert Samsung lizenzpflichtige Apps.
    // payload: der Schluessel, oder { key, onProgress, quiet } (siehe launchApp).
    launchByKey({ commit, dispatch, state, rootState }, payload) {
      const opts = payload && typeof payload === 'object' ? payload : { key: payload };
      const wanted = String(opts.key || '').trim().toLowerCase();
      if (!wanted) return Promise.reject(new Error('kein App-Schluessel'));
      if (!state.prepared) {
        const device = (rootState && rootState.device) || {};
        commit('setApps', pickCastApps(rootState, null));
        commit('setToken', device.castToken || DEFAULT_CAST_TOKEN);
      }
      // Zuerst die fuer dieses Geraet konfigurierte Liste (device.castApps kann
      // App-Ids ueberschreiben), dann der Katalog: steht die Kachel im
      // Hauptmenue, ist sie gewollt - auch wenn die App nicht zusaetzlich als
      // Cast-Kachel eingerichtet ist.
      let app = state.apps.find((a) => a.key === wanted);
      if (!app) app = CAST_APP_CATALOG.find((a) => a.key === wanted);
      if (!app) {
        dispatch(
          'notification/error',
          { title: 'Cast/Launch', message: 'Unbekannte App: ' + wanted },
          { root: true }
        );
        return Promise.reject(new Error('unbekannte App: ' + wanted));
      }
      return dispatch('prepare').then(() =>
        dispatch('launchApp', { app: app, onProgress: opts.onProgress, quiet: opts.quiet })
      );
    },
    // Bereitet das Pairing gemaess Strategie vor und liefert zurueck, ob ein
    // eigener QR angezeigt werden soll.
    //   softap-qr      -> eigenen SoftAP konfigurieren (Samsung)
    //   network-qr     -> SSID/Key des verbundenen WLANs lesen (LG < 25)
    //   lg-native-cast -> nichts vorbereiten, der TV macht das Pairing selbst
    //   screenshare    -> kein QR
    configurePairing({ commit, dispatch, state }) {
      const strategy = state.strategy;
      if (strategy === 'softap-qr') {
        // Steht nach einem Standby noch die Erneuerung des SoftAP-Passworts
        // aus, passiert sie JETZT - vor dem Schreiben der Konfiguration und vor
        // dem QR. Hierher gehoert sie, weil die Firmware-Aufrufe den Thread
        // blockieren: auf der Cast-Seite steht dafuer "Verbindung wird
        // vorbereitet", auf der Home-Seite hat ein blockierter Thread den TV
        // einfrieren lassen.
        // Hat der Standby den Wechsel schon erledigt, entfaellt er hier.
        //
        // Das ist der eigentliche Gewinn: rotateSoftAP zykelt den Hotspot
        // (soft_ap 0 -> 1), um den neuen Schluessel wirksam zu machen - und
        // wirft dabei jedes verbundene Handy hinaus. Im Standby genuegte ein
        // setProperty, und der Uebergang kam beim Einschalten gratis.
        const rotatedAlready = softApRotatedInStandby;
        if (rotatedAlready) {
          softApRotatedInStandby = false;
          console.log('[cast] Passwortwechsel war schon im Standby - kein Hotspot-Neustart noetig');
        }
        const rotate =
          !rotatedAlready && state.clearPending && state.softApActive && !state.softApRotated
            ? dispatch('rotateSoftAP')
                .catch(() => false)
                .then(() => {
                  // Erledigt - fuer diesen Gastwechsel nicht noch einmal.
                  commit('setSoftApRotated', true);
                  return dispatch('settleClearPending');
                })
            : Promise.resolve(false);
        return rotate.then(() => dispatch('configureSoftAP')).then((info) =>
          // Erst den Hotspot notfalls zurueckholen, DANN entscheiden, ob ein QR
          // gezeigt wird. Laeuft er, kostet ensureSoftApUp nichts - es prueft
          // isSoftAPEnabled und ist sofort fertig.
          Cast.ensureSoftApUp()
            .then(() => Cast.softApNotSending())
            .then((down) => {
              // Kein QR, wenn der Hotspot nicht sendet. Er waere ein Code, der
              // ins Nichts fuehrt: SSID und Passwort stimmen, nur nimmt sie
              // niemand entgegen. Am U800F genau so aufgetreten - isSoftAPEnabled
              // false, Signalstaerke 0xFFFFFFFF - und am BU800 mit im
              // Service-Menue abgeschaltetem Hotspot.
              //
              // Auf BEIDEN Plattformen: auf LG beantwortet die Property soft_ap
              // dieselbe Frage. null heisst "keine Auskunft" - dann wird nichts
              // behauptet und der QR erscheint wie bisher.
              if (down === true) console.log('[cast] Hotspot sendet nicht - kein QR, sondern Hinweis');
              return { qr: down !== true, info: info, hotspotDown: down === true };
            })
        );
      }
      if (strategy === 'network-qr') {
        return Cast.getNetworkQrCredentials().then((creds) => {
          commit('setSoftAP', Object.assign({ hidden: false }, creds));
          return { qr: !!creds.ssid, info: creds };
        });
      }
      return Promise.resolve({ qr: false, info: null });
    },
    // Konfiguriert den SoftAP (SSID default = TV-Name) und legt die effektiven
    // Zugangsdaten fuer die QR-Erzeugung ab.
    //
    // ACHTUNG LG: dort ist die SoftAP-SSID die Property tv_name - also zugleich
    // der Geraetename, unter dem der TV in Cast-/AirPlay-Geraetelisten steht.
    // Ein Aufruf hier benennt den TV also mit um. Soll das nicht passieren,
    // gehoert eine feste SSID in device.softAP.ssid (die hat Vorrang).
    configureSoftAP({ commit, state, rootState }, config) {
      const device = (rootState && rootState.device) || {};
      const softApCfg = device.softAP || {};
      // Individuelle SSID (Raumname + Zufall), einmalig pro Sitzung. Eine explizit
      // konfigurierte device.softAP.ssid hat Vorrang. Raumname bevorzugt aus dem
      // Device-State, sonst aus der Registrierung (localStorage) als Fallback.
      if (!sessionSoftApSsid) {
        const regRoom = (Registration.getData && Registration.getData.room) || '';
        const roomName = device.room || regRoom;
        rememberSsid(softApCfg.ssid || buildRoomSsid(roomName));
      }
      // Passwort ebenso: einmal erzeugt, gemerkt, danach immer dasselbe - bis
      // zum Gastwechsel. Vom TV zurueckzulesen ist es nicht.
      if (!sessionSoftApKey) rememberKey(softApCfg.key || buildSoftApPassword());
      const merged = Object.assign(
        { security: 'WPA', hidden: false },
        softApCfg,
        // Kennung und Passwort kommen aus UNSEREM Merker, nicht vom Geraet.
        { ssid: sessionSoftApSsid, key: sessionSoftApKey },
        config || {}
      );
      const info = Cast.configureSoftAP(merged);
      // Hat der Helfer die Konfiguration vom Geraet UEBERNOMMEN, statt sie zu
      // schreiben (weil ein Schreibzugriff ohne Neustart nicht wirksam werden
      // kann), muss unser Merker nachziehen. Sonst zeigt die Seite beim
      // naechsten Start wieder den alten Wert - und der Gast bekommt ein
      // Passwort, das der Hotspot nicht kennt.
      //
      // Nur der SCHLUESSEL. Die SSID nicht: die traegt auf manchen Firmwares ein
      // Praefix ('[TV]devo-WUN9'), und als gemerkter Schreibwert wuerde daraus
      // beim naechsten Mal ein doppeltes Praefix.
      if (info && info.adopted && info.key && info.key !== sessionSoftApKey) {
        console.log('[cast] SoftAP-Passwort vom Geraet uebernommen');
        rememberKey(info.key);
      }
      // Ab jetzt kennt ein Gast Kennung und Passwort - beim Gastwechsel neu vergeben.
      commit('setSoftApActive', true);
      commit('setSoftAP', Object.assign({ hidden: merged.hidden }, info));

      // Gegenpruefen, ob der TV SSID UND Passwort wirklich uebernommen hat. Der
      // QR wird erst nach diesem Schritt gebaut (configurePairing wartet
      // darauf), damit er im Zweifel die ECHTEN Zugangsdaten zeigt und nicht die
      // gewuenschten. Gibt das Geraet nichts heraus (readable/keyReadable
      // false), bleibt es beim gemerkten Wert - das ist keine Abweichung.
      return Cast.verifySoftAP(info.ssid, info.key).then((v) => {
        commit('setSoftApVerify', v);
        const fix = {};
        if (v && v.readable && !v.matched) fix.ssid = v.ssid;
        if (v && v.keyReadable && !v.keyMatched) {
          fix.key = v.key;
          // Auch merken: sonst schreibt der naechste Aufruf wieder den Wert,
          // den das Geraet gerade abgelehnt hat.
          rememberKey(v.key);
        }
        if (!Object.keys(fix).length) return info;
        commit('setSoftAP', fix);
        return Object.assign({}, info, fix);
      });
    },
    // Verbindungen am SoftAP beobachten, damit die Pairing-Seite melden kann,
    // dass das Smartphone tatsaechlich angekommen ist. Ohne diese Rueckmeldung
    // steht der Gast vor einem QR-Code und weiss nicht, ob es geklappt hat.
    //
    // Nur sinnvoll bei eigenem SoftAP: beim network-qr haengt das Handy im
    // Hausnetz, davon bekommt der TV nichts mit.
    //
    // Einmal registriert, bleibt der Listener bewusst aktiv - auch wenn der Gast
    // die Pairing-Ansicht verlaesst. Der Listener meldet nur AENDERUNGEN, es gibt
    // keine Abfrage des Ist-Zustands. Wuerde man ihn beim Verlassen abmelden,
    // waere die Geraeteliste beim Zurueckkommen leer und die Seite zeigte
    // "warte auf Verbindung", obwohl das Handy noch haengt (Weg Cast -> YouTube
    // -> zurueck zu Cast). Es ist genau ein Listener, keine DOM-Bindung.
    //
    // AUSNAHME seit 06.10.2026: der Listener wird bei jedem Oeffnen NEU
    // registriert. Am BU800 (Firmware T-NKLBDEUC-1720.7) kamen nach einem
    // Wechsel des SoftAP-Passworts (Hotspot neu aufgebaut) keine Ereignisse
    // mehr - der alte Listener war noch eingetragen, die Firmware lieferte an
    // ihn aber nichts mehr. Die Geraeteliste geht dabei verloren; diese
    // Firmware meldet ohnehin nur 1/0 ohne Geraet, eine Liste gibt es dort nicht.
    watchSoftAP({ commit, state, dispatch }) {
      if (softApListener || softApClientListener || softApPoll) {
        console.log('[cast] SoftAP-Listener wird neu registriert');
        dispatch('unwatchSoftAP');
      }
      if (state.strategy !== 'softap-qr') {
        commit('setPairing', { watching: false, listening: false, connected: false, clients: 0, name: '', raw: '' });
        return false;
      }
      // Die eigentliche Quelle fuer "wer hat sich verbunden".
      // addSoftAPStateChangeListener meldet den Zustand des HOTSPOTS, nicht
      // seine Clients: am U800F (Tizen 9.0) war er registriert und hat nie
      // gefeuert, obwohl ein Handy hing. Deshalb zuerst der Client-Listener.
      const hasClientListener = Cast.hasSoftAPClientInfoListener();
      const hasListener = Cast.hasSoftAPStateListener();
      // Zweite Quelle: die Abfrage des Ist-Zustands. Der Listener meldet nur
      // AENDERUNGEN - war das Handy schon verbunden, bevor die Cast-Seite ihn
      // registriert hat, kommt nie ein Ereignis und die Anzeige haengt fuer
      // immer auf "warte auf Verbindung". Genau dieses Symptom loest die
      // Abfrage, deshalb reicht schon eine der beiden Quellen zum Beobachten.
      const hasQuery = Cast.hasSoftAPClientQuery();
      if (!hasClientListener && !hasListener && !hasQuery) {
        commit('setPairing', { watching: false, listening: false, connected: false, clients: 0, name: '', raw: 'kein Listener' });
        return false;
      }
      softApClients = {};
      // Grundlinie: was die Kandidaten JETZT liefern, wo garantiert noch kein
      // Handy haengt.
      //
      // Ohne sie ist die Abfrage in beide Richtungen unbrauchbar. Eine Funktion
      // wie getSoftapDeviceMode, die konstant 1 zurueckgibt, laese sich als
      // "1 Client" lesen - die Seite spraenge sofort zu den Kacheln, ohne dass
      // ein Handy da ist. Gemeldet wird deshalb nur, was sich gegenueber diesem
      // Ausgangswert GEAENDERT hat. Welche Funktion die richtige ist, gibt
      // Samsung nicht her; sie ist genau die, deren Wert sich bewegt, sobald
      // sich jemand verbindet.
      softApBaseline = {};
      const base = Cast.getSoftAPClients();
      for (let b = 0; b < base.length; b++) {
        softApBaseline[base[b].fn] = Cast.readSoftAPState(base[b].value).raw;
      }
      console.log('[cast] SoftAP-Grundlinie:', JSON.stringify(softApBaseline));

      // Reihenfolge: erst der Listener (der setzt den Ausgangszustand "warte auf
      // Verbindung"), dann die Abfrage. Umgekehrt wuerde der Ausgangszustand
      // gleich wieder ueberschreiben, was die Abfrage schon gefunden hat.
      // Beide Listener werten ihr Ereignis gleich aus. Die Nutzlast ist auf
      // keiner der beiden Schnittstellen dokumentiert; readSoftAPState kommt
      // mit Zaehler, Zustands-String und Client-Objekt zurecht, und der
      // Rohwert landet in pairing.raw - auf /info als "Letzte SoftAP-Meldung"
      // ablesbar. Ohne den waere am Geraet nicht zu sehen, ob ein Ereignis
      // ueberhaupt kam oder nur anders aussah als erwartet.
      const onEvent = (source, payload) => {
        const info = Cast.readSoftAPState(payload);
        softApEventSeen = true;
        console.log('[cast] SoftAP-Ereignis (' + source + '):', info.raw, '->', info.connected ? 'verbunden' : 'getrennt');

        // Der TV meldet pro Geraet ein eigenes Ereignis - ueber die MAC
        // mitzaehlen, damit ein "getrennt" die Anzeige nicht loescht, solange
        // noch ein anderes Geraet haengt.
        const merged = Cast.mergeSoftAPClients(softApClients, info);
        softApClients = merged.known;

        // Ein Ereignis ist gekommen - dieses Geraet meldet Verbindungen also
        // wirklich. Ab jetzt darf die Seite eine Verbindungszeile zeigen, und bei
        // connected springt der Watcher in Cast.vue zu den Kacheln.
        //
        // Auf einem BU800 (Tizen 6.5) kam pro Client ein eigenes Ereignis MIT
        // Namen; mergeSoftAPClients zaehlt sie ueber die MAC mit und zeigt das
        // zuletzt hinzugekommene. Das funktioniert unveraendert weiter - auf
        // Tizen 9.0 kommen nur keine Ereignisse mehr.
        commit('setPairing', {
          watching: true,
          listening: true,
          connected: merged.connected,
          clients: merged.clients,
          name: merged.name,
          raw: source + ': ' + info.raw,
        });
      };

      let listening = false;
      if (hasClientListener) {
        softApClientListener = (payload) => onEvent('ClientInfo', payload);
        const cid = Cast.addSoftAPClientInfoListener(softApClientListener);
        if (cid === -1) {
          softApClientListener = null;
          console.log('[cast] addSoftAPClientInfoListener abgelehnt');
        } else {
          listening = true;
          commit('setPairing', { listening: true, connected: false, clients: 0, name: '', raw: '' });
        }
      }
      if (hasListener) {
        softApListener = (payload) => onEvent('State', payload);
        const id = Cast.addSoftAPStateChangeListener(softApListener);
        if (id === -1) {
          softApListener = null;
          if (!listening) {
            commit('setPairing', { listening: false, connected: false, clients: 0, name: '', raw: 'Listener abgelehnt' });
          }
        } else {
          listening = true;
          commit('setPairing', { listening: true, connected: false, clients: 0, name: '', raw: '' });
        }
      }

      // Das Ergebnis der Registrierung in pairing.raw festhalten. Auf /info
      // ("Letzte SoftAP-Meldung") war bisher nicht zu unterscheiden, ob ein
      // Listener abgelehnt wurde oder nur nichts gemeldet hat - beides sah wie
      // "noch keine" aus.
      if (listening) {
        const reg =
          'registriert: ' +
          (softApClientListener ? 'ClientInfo' : 'ClientInfo nein') +
          ' / ' +
          (softApListener ? 'State' : 'State nein');
        console.log('[cast] SoftAP-Listener ' + reg);
        commit('setPairing', { listening: true, connected: false, clients: 0, name: '', raw: reg });
      }

      if (!hasQuery) return listening;

      const poll = () => {
        // Alle Kandidaten durchsehen und den ERSTEN nehmen, der Geraete meldet.
        // Welche Funktion die richtige ist, gibt Samsung nicht her; sie ist
        // genau die, deren Wert Geraete zeigt, sobald welche haengen.
        //
        // Nur Treffer werden gemeldet. Eine 0 gilt NICHT als "getrennt": das
        // Format ist nicht dokumentiert, und eine falsch gedeutete 0 wuerde eine
        // per Ereignis korrekt erkannte Verbindung wieder ausknipsen.
        const all = Cast.getSoftAPClients();
        for (let i = 0; i < all.length; i++) {
          const info = Cast.readSoftAPState(all[i].value);
          if (!info.clients) continue;
          // Unveraendert gegenueber der Grundlinie = kein Client, sondern ein
          // konstanter Wert, der nur wie einer aussieht.
          if (softApBaseline[all[i].fn] === info.raw) continue;
          console.log('[cast] SoftAP-Abfrage bewegt sich:', all[i].fn, softApBaseline[all[i].fn], '->', info.raw);
          commit('setPairing', {
            watching: true,
            listening: true,
            connected: true,
            clients: info.clients,
            name: (info.client && info.client.name) || '',
            raw: all[i].fn + ': ' + info.raw,
          });
          return;
        }
      };
      if (!listening) {
        commit('setPairing', { listening: true, connected: false, clients: 0, name: '', raw: 'nur Abfrage, kein Listener' });
      }
      poll();
      softApPoll = setInterval(poll, SOFTAP_POLL_MS);
      return true;
    },
    unwatchSoftAP({ commit }) {
      if (softApClientListener) {
        Cast.removeSoftAPClientInfoListener(softApClientListener);
        softApClientListener = null;
      }
      if (softApListener) {
        Cast.removeSoftAPStateChangeListener(softApListener);
        softApListener = null;
      }
      if (softApPoll) {
        clearInterval(softApPoll);
        softApPoll = null;
      }
      softApClients = {};
      softApBaseline = {};
      commit('setPairing', { watching: false, listening: false, connected: false, clients: 0, name: '', raw: '' });
      return true;
    },
    // Firmware-Update anstossen (Datei muss per URL erreichbar sein; Upload in
    // den internen Speicher erfolgt spaeter ueber den SocketAdmin).
    updateFirmware({ commit, dispatch }, fw) {
      commit('setFirmware', { active: true, progress: 0, error: null });
      return Cast.updateFirmware(fw, (data) => {
        commit('setFirmware', { progress: data });
        // Auch an den Admin melden: das socket-Modul drosselt und haengt es an
        // die state-Meldung. Art und Dateiname stehen dort schon, hier reicht
        // der Fortschritt.
        dispatch(
          'socket/noteRollout',
          { rollout: { phase: 'running', progress: readProgressPercent(data) } },
          { root: true }
        );
      })
        .then((r) => {
          return r;
        })
        .catch((err) => {
          commit('setFirmware', { active: false, error: String((err && err.message) || err) });
          return Promise.reject(err);
        });
    },
    // Settings-Cloning anwenden (kann fehlende Apps via Clonefile nachziehen).
    restoreClone({ commit, dispatch }, cloneFilePath) {
      commit('setClone', { active: true, progress: 0, error: null });
      return Cast.restoreClone(cloneFilePath, (data) => {
        commit('setClone', { progress: data });
        dispatch(
          'socket/noteRollout',
          { rollout: { phase: 'apply', progress: readProgressPercent(data) } },
          { root: true }
        );
      })
        .then((r) => r)
        .catch((err) => {
          commit('setClone', { active: false, error: String((err && err.message) || err) });
          return Promise.reject(err);
        });
    },
    clearCredentials(_, value) {
      return Cast.clearCredentials(value || 'ON');
    },
    // Der Befehl clear_credentials aus dem SocketAdmin - und zwar so, dass er
    // tut, was er sagt.
    //
    // Vorher lief er auf Cast.clearCredentials('ON') hinaus, und das ist auf
    // Samsung NUR das Scharfschalten fuer den naechsten Neustart. Wer am Admin
    // "Anmeldedaten loeschen" drueckte, bekam also eine Vormerkung und keine
    // Loeschung - ohne dass irgendwo stand, dass nichts passiert ist.
    //
    // Jetzt: scharf schalten (kostet nichts und ueberlebt einen Stromausfall)
    // UND sofort loeschen, wo das Geraet es kann. Das Ergebnis geht als Antwort
    // zurueck, damit am Admin sichtbar ist, was tatsaechlich geschah.
    //
    // Das SoftAP-Passwort bleibt unberuehrt: der Befehl heisst
    // clear_credentials, und der Netzzugang ist eine andere Sache
    // (rotateSoftAP, ausgeloest beim Oeffnen der Cast-Seite).
    clearCredentialsCommand({ commit, dispatch, state }, value) {
      const raw = String(value || 'ON').toUpperCase();
      // Dritter Modus: NUR den Checkout-Snapshot nehmen, nichts loeschen.
      //
      // Warum an diesem Befehl und nicht am eigenen take_checkout_snapshot: das
      // eigene Kommando lief am Geraet in "command response timed out" - der
      // Socket-Server kennt es nicht und ordnet die Antwort keinem Kommando zu.
      // clear_credentials kommt dagegen nachweislich durch, und thematisch
      // gehoert der Snapshot ohnehin hierher: er ist der Zustand, auf den
      // dieser Befehl den TV spaeter zurueckSETZT.
      //
      // NUR nehmen, nicht zusaetzlich loeschen: ein Checkout unmittelbar nach
      // dem Snapshot setzt auf den gerade gesicherten Stand zurueck und ist
      // damit wirkungslos. Erst sichern, spaeter loeschen.
      //
      // Mit force, weil der Aufrufer am Geraet steht: der automatische Weg
      // lehnt auf jedem schon benutzten TV ab (softApActive ueberdauert als
      // persistierter Merker). Die Verantwortung fuer "der TV ist sauber"
      // liegt damit beim Absender - siehe cast/takeCheckoutSnapshot.
      if (raw === 'SNAPSHOT') {
        return dispatch('takeCheckoutSnapshot', { force: true }).then((r) => ({
          ok: !!(r && r.ok),
          value: 'SNAPSHOT',
          armed: false,
          immediate: false,
          deleted: false,
          snapshot: !!(r && r.taken),
          detail: (r && r.detail) || 'Snapshot',
        }));
      }
      const mode = raw === 'OFF' ? 'OFF' : 'ON';
      if (mode === 'OFF') {
        // Ausdruecklich entschaerfen - eine legitime Admin-Aktion, etwa fuer ein
        // Vorfuehrgeraet, das seine Anmeldungen behalten soll.
        const off = Cast.clearCredentials('OFF');
        console.log('[cast] Loeschen beim Neustart ENTSCHAERFT:', off);
        return Promise.resolve({
          ok: !!off,
          value: mode,
          armed: false,
          immediate: false,
          deleted: false,
          detail: off ? 'Loeschen beim Neustart abgeschaltet' : 'Geraet nimmt den Befehl nicht an',
        });
      }

      const armed = Cast.armClearCredentials();
      // Auch die Daten des Google-Cast-Dienstes (Samsung googlecast.clearData).
      Cast.clearCastData().catch(() => false);
      const immediate = Cast.hasClearCredentialsNow();
      if (!immediate) {
        console.log(
          '[cast] clear_credentials: scharf geschaltet, aber dieses Geraet kann nicht sofort loeschen'
        );
        return Promise.resolve({
          ok: !!armed,
          value: mode,
          armed: !!armed,
          immediate: false,
          deleted: false,
          detail: armed
            ? 'scharf geschaltet - die Anmeldedaten verschwinden beim naechsten Neustart des TV'
            : 'Geraet nimmt den Befehl nicht an',
        });
      }
      return Cast.clearCredentialsNow()
        .then((r) => {
          // Diese Rueckmeldung liest der Betreiber im Admin - hier gehoert der
          // Vorbehalt hin. Auf LG quittiert requestCheckout auch dann, wenn es
          // mangels Checkout-Snapshot gar nichts zurueckzusetzen gibt (siehe
          // Cast/LG.js clearCredentialsNow). "deleted" ist dort eine Quittung,
          // kein Nachweis.
          const verified = Cast.isClearCredentialsVerified();
          console.log(
            '[cast] clear_credentials: Aufruf angenommen' +
            (verified === true
              ? ''
              : verified === null
                ? ' - Wirkung auf diesem Geraet nicht geprueft'
                : ' - WIRKUNG AUF DIESEM GERAET NICHT NACHWEISBAR') + ':',
            r
          );
          commit('setSessionActive', false);
          if (!state.softApActive) commit('setClearPending', false);
          return {
            ok: true,
            value: mode,
            armed: !!armed,
            immediate: true,
            deleted: true,
            verified: verified,
            detail: verified === true
              ? 'sofort geloescht (' + String(r) + ')'
              : verified === null
                ? 'Aufruf angenommen (' + String(r) + '), Wirkung auf diesem Geraet nicht geprueft'
                : 'Aufruf angenommen, Wirkung nicht nachweisbar - auf diesem Geraet setzt der' +
                ' Checkout auf einen Snapshot zurueck, und es wurde nie einer genommen (' +
                String(r) + ')',
          };
        })
        .catch((err) => {
          const detail = errText(err);
          console.log('[cast] clear_credentials: Sofort-Loeschen fehlgeschlagen:', detail);
          return {
            ok: !!armed,
            value: mode,
            armed: !!armed,
            immediate: true,
            deleted: false,
            detail: 'Sofort-Loeschen fehlgeschlagen (' + detail + ') - scharf geschaltet bleibt es',
          };
        });
    },
    // Schaltet das Loeschen der App-Anmeldedaten beim naechsten Neustart scharf
    // (Samsung: deleteAppPrivateInfo("ON")). Idempotent und billig - deshalb bei
    // jedem App-Start erneut, damit ein zwischenzeitliches Zuruecksetzen der
    // Einstellung nicht dauerhaft durchschlaegt.
    // Ein Cast vom Handy lief (Google Cast stand auf CASTING), ohne dass eine
    // unserer Kacheln gestartet wurde. Auch darueber kann sich der Gast in
    // einer TV-App angemeldet haben - also dieselbe Aufraeumpflicht wie beim
    // App-Start: Sitzungsmerker setzen und das Loeschen scharf schalten.
    noteCastSession({ commit, dispatch, state }) {
      if (state.sessionActive) return;
      console.log('[cast] Cast vom Handy erkannt - Gastsitzung vermerkt');
      commit('setSessionActive', true);
      dispatch('armClearCredentials');
    },
    armClearCredentials() {
      try {
        const ok = Cast.armClearCredentials();
        console.log('[cast] clear-credentials fuer Neustart scharf geschaltet:', ok);
        return ok;
      } catch (e) {
        console.log('[cast] armClearCredentials error', e);
        return false;
      }
    },
    // Loescht die Anmeldedaten der OTT-Apps SOFORT. Wird beim Ausschalten und
    // beim Einschalten aufgerufen.
    //
    // Warum beides: "Power Off" ist auf dem Hotel-TV meist nur Standby - es gibt
    // keinen Reboot, das scharf geschaltete Loeschen beim Neustart greift also
    // nicht. Und beim Ausschalten bleibt oft zu wenig Zeit, bis die App
    // suspendiert wird; deshalb der zweite Versuch beim Einschalten, solange der
    // Merker noch steht.
    // Neues SoftAP-Passwort beim Gastwechsel.
    //
    // Ohne das bliebe der Schluessel auf Samsung fuer immer derselbe
    // (configureSoftAP bevorzugt den bereits gespeicherten), und ein
    // abgereister Gast koennte sich weiter mit dem SoftAP des Zimmers
    // verbinden. Laeuft im selben Vorgang wie das Loeschen der Anmeldedaten.
    //
    // Der Merker faellt erst, wenn der TV den neuen Schluessel bestaetigt hat -
    // sonst wird beim naechsten Ein-/Ausschalten erneut rotiert.
    rotateSoftAP({ commit, state }) {
      if (!state.softApActive) return Promise.resolve(false);
      // Das neue Passwort entsteht HIER und wird gemerkt, bevor es geschrieben
      // wird - vom TV zurueckzulesen ist es nicht.
      const next = buildSoftApPassword();
      return Cast.rotateSoftAP(next).then((r) => {
        // Geschrieben, aber Hotspot nicht hochgekommen: den Schluessel trotzdem
        // merken. Auf dem Geraet gilt ab jetzt der neue - die Anzeige muss dazu
        // passen, sobald der Hotspot zurueck ist. Der Merker softApActive bleibt
        // dabei stehen, damit der naechste Zyklus es erneut versucht.
        if (r && r.written && !r.ok) {
          rememberKey(next);
          commit('setSoftAP', { key: next });
        }
        if (r && r.ok) {
          rememberKey(next);
          // Die Kennung bleibt STEHEN. Sie zu verwerfen hiess: die naechste
          // Cast-Seite vergibt eine neue SSID, schreibt sie auf den TV und
          // startet dafuer den Hotspot neu - bei jedem Gastwechsel. Jeder dieser
          // Neustarts ist eine Gelegenheit, den AP zu verlieren (am Geraet
          // passiert), und die SSID ist kein Geheimnis: sie traegt den
          // Raumnamen. Neu vergeben wird nur das Passwort.
          commit('setSoftApActive', false);
          commit('setSoftAP', { key: next });
          console.log('[cast] SoftAP-Zugang fuer neuen Gast erneuert');
          return true;
        }
        // Nicht merken, wenn der TV es nicht uebernommen hat - sonst zeigten
        // wir ein Passwort an, das auf dem Geraet gar nicht gilt.
        console.log('[cast] SoftAP-Passwort nicht erneuert:', (r && r.detail) || 'unbekannt');
        return false;
      });
    },
    // Bereitet einen Passwortwechsel vor, den erst der Neustart wirksam macht.
    //
    // Nur fuer Geraete, die ihren Hotspot nicht neu starten koennen. Dort
    // verweigert rotateSoftAP den Wechsel - zu Recht, denn ohne Neustart wuerde
    // die Cast-Seite ein Passwort zeigen, das nicht gilt. Mit einem Neustart,
    // den wir selbst ausloesen, geht es aber: Schluessel schreiben, neu
    // starten, und nach dem Hochlaufen gilt und gilt angezeigt derselbe Wert.
    //
    // Gerufen unmittelbar vor device/reboot im Standby-Neustart. Auf Geraeten,
    // die rotieren koennen, ist das ein No-op - dort hat die Cast-Seite die
    // Rotation schon erledigt.
    stageSoftApRotation({ state }) {
      if (!state.softApActive) return Promise.resolve(false);
      if (Cast.canRestartSoftAp()) return Promise.resolve(false);
      const next = buildSoftApPassword();
      return Cast.stageSoftApKey(next).then((ok) => {
        if (!ok) {
          console.log('[cast] Passwortwechsel fuer den Neustart nicht vorbereitet - Schreibzugriff fehlgeschlagen');
          return false;
        }
        writeStagedKey(next);
        console.log('[cast] Neues SoftAP-Passwort geschrieben, gilt nach dem Neustart');
        return true;
      });
    },
    // Der Passwortwechsel IM STANDBY - ausgeloest, sobald der Beobachter sieht,
    // dass soft_ap von selbst auf 0 gefallen ist (siehe Cast/LG.js).
    //
    // WARUM HIER UND NICHT AUF DER CAST-SEITE: dort lief bisher rotateSoftAP,
    // und das zykelt den Hotspot (soft_ap 0 -> 1), um den neuen Schluessel
    // wirksam zu machen. Jeder Zyklus wirft aber jedes verbundene Handy aus dem
    // Netz, und er steht im Verdacht, die Cast-Bindung zu kosten. Im Standby
    // braucht es ihn gar nicht: der Uebergang 0 -> 1 passiert beim Einschalten
    // ohnehin, ein blosses setProperty genuegt.
    //
    // Der Schluessel gilt damit erst ab dem Einschalten - bis dahin sendet der
    // Hotspot weiter mit dem alten. Genau dafuer ist writeStagedKey da;
    // befoerdert wird er beim Aufwachen (noteStandbyIfSoftApDown).
    //
    // NICHT VERMESSEN ist, ob HCAP im Standby noch Schreibzugriffe annimmt -
    // lesend antwortet es dort. Scheitert es, bleibt alles beim Alten: der
    // Wechsel passiert dann wie bisher beim Oeffnen der Cast-Seite.
    rotateSoftApInStandby({ state }) {
      if (!state.softApActive) return Promise.resolve(false);
      if (readStagedKey()) {
        // Schon einer unterwegs - nicht bei jedem Flackern einen neuen.
        return Promise.resolve(false);
      }
      const next = buildSoftApPassword();
      return Cast.stageSoftApKey(next).then((ok) => {
        if (!ok) {
          console.log('[cast] Passwortwechsel im Standby nicht moeglich - bleibt beim Oeffnen der Cast-Seite');
          return false;
        }
        writeStagedKey(next);
        console.log('[cast] SoftAP-Passwort im Standby erneuert, gilt ab dem Einschalten');
        return true;
      });
    },
    // Schliesst den Vorgang ab, den ein Standby-Neustart begonnen hat.
    //
    // Lief der Neustart, hat die Firmware die Anmeldedaten waehrend des
    // Hochlaufens geloescht (deleteAppPrivateInfo war scharf). Erst hier darf
    // sessionActive also fallen - auf Geraeten ohne Sofort-Loeschen gibt es
    // keine andere Bestaetigung, und ohne sie wuerde der TV nach jedem Standby
    // erneut neu starten.
    //
    // Beim App-Start aufgerufen. Ein blosser Reload loest nichts aus: der
    // Merker wird ausschliesslich unmittelbar vor dem Neustart gesetzt.
    settleAfterRebootClear({ commit, dispatch, state }) {
      // Wurde die App unmittelbar vor dem geplanten Neustart vom eigenen
      // launch neu gestartet, ist der Neustart NICHT gelaufen - dann wird er
      // hier nachgeholt, statt ihn als erledigt zu verbuchen.
      const pendingAge = readRebootPendingAge();
      if (pendingAge !== null) {
        writeRebootPending(false);
        if (pendingAge < REBOOT_RESUME_MS && readRebootClearFlag()) {
          console.log(
            '[cast] App ' + Math.round(pendingAge / 1000) + ' s vor dem Neustart neu gestartet ' +
              '(BringToFront) - Neustart wird jetzt nachgeholt'
          );
          // Als geplanter Neustart fuehren: schaltet der Gast den TV in diesen
          // Sekunden ein, bricht clearGuestCredentials (remotepower NORMAL)
          // ihn ueber cancelStandbyReboot ab. Vorher lief dieser Timer an
          // allem vorbei.
          standbyRebootTimer = setTimeout(() => {
            standbyRebootTimer = null;
            // Letzte Kontrolle wie beim geplanten Neustart.
            if (tvIsOn()) {
              console.log('[cast] nachgeholter Neustart verworfen - der TV ist an');
              writeRebootClearFlag(false);
              return;
            }
            // Erst JETZT loggen: die Zeile oben faellt vor die Socket-Anmeldung
            // und erreicht das Live-Log nie. Diese hier zeigt dort, welcher
            // Stand mit welcher Pause laeuft.
            console.log('[cast] nachgeholter Neustart nach ' + REBOOT_RESUME_DELAY_MS + ' ms Pause');
            if (REBOOT_FRONT) WakeAlarm.set('nachgeholter Standby-Neustart');
            rebootCommitted = true;
            dispatch('device/reboot', null, { root: true }).then((r) => {
              if (!r || r.ok !== false) return;
              dispatch('rollbackStandbyReboot', r);
            });
          }, REBOOT_RESUME_DELAY_MS);
          return false;
        }
      }
      if (!readRebootClearFlag()) return false;
      writeRebootClearFlag(false);
      console.log('[cast] Neustart zum Loeschen ist gelaufen - Anmeldedaten gelten als entfernt');
      commit('setSessionActive', false);
      // Und der vorbereitete Schluessel gilt jetzt: der TV hat seinen Hotspot
      // beim Hochlaufen mit der gespeicherten Konfiguration neu aufgebaut.
      // Erst JETZT darf er angezeigt werden - vorher sendete der Hotspot noch
      // mit dem alten.
      // Rueckfall auf den Wert, den das GERAET gespeichert hat.
      //
      // Der vorbereitete Schluessel liegt im localStorage, und der wurde
      // Millisekunden vor einem Neustart geschrieben - eine noch nicht auf die
      // Platte gebrachte Aenderung kann dabei verloren gehen. Genau das ist
      // passiert: der Reboot-Merker war da, der Schluessel nicht. Nach dem
      // Neustart sendet der Hotspot ohnehin mit dem gespeicherten Wert, also
      // ist er die verlaesslichere Quelle.
      const staged = readStagedKey() || Cast.readSoftApKey();
      if (staged) {
        writeStagedKey(null);
        rememberKey(staged);
        commit('setSoftAP', { key: staged });
        commit('setSoftApActive', false);
        console.log('[cast] Vorbereitetes SoftAP-Passwort gilt ab jetzt');
      }
      dispatch('settleClearPending');
      return true;
    },
    // Haelt den Hotspot an - ein Versuch pro Aufruf.
    //
    // Solange im Hauptmenue eine Cast-App steht, soll der Hotspot laufen. Er
    // faellt aber aus: die Firmware verliert ihn, das Service-Menue wird
    // umgestellt, oder ein frueherer Fehler hat ihn liegengelassen. Deshalb
    // wird nachgeholfen - wiederholt, aber immer nur mit EINEM Aufruf, weil
    // enableSoftAP synchron ist und den Thread je Firmware bis zu zehn Sekunden
    // haelt.
    //
    // Rueckgabe: true = laeuft, false = laeuft nicht, null = keine Auskunft.
    // Die Cast-Flags des Geraets sicherstellen (DIAL, Screen Share, asl_mode).
    //
    // DER ANLASS: nach einem Neustart verband sich das Handy weiterhin mit dem
    // SoftAP - gleiche SSID, gleiches Passwort -, aber die YouTube-App fand
    // keinen Fernseher mehr zum Verbinden. Der Hotspot allein reicht eben
    // nicht: gefunden wird der TV ueber DIAL, und dieses Flag lief bisher NUR
    // in cast/prepare mit, also erst beim Oeffnen der Cast-Seite. Wer nach dem
    // Neustart direkt vom Handy castet, kommt dort nie vorbei.
    //
    // Zweiter Anlass: requestCheckout setzt den TV auf den Checkout-Snapshot
    // zurueck. Stand DIAL beim Snapshot auf 0, schaltet jedes Aufraeumen die
    // Auffindbarkeit wieder ab - deshalb laeuft das hier auch nach jedem
    // Loeschen.
    ensureCastFlags({ dispatch }) {
      // Hier mitstarten, weil Home.vue diese Aktion beim App-START ruft: der
      // Beobachter muss laufen, BEVOR der TV ausgeht. cast/init kaeme erst beim
      // Oeffnen der Cast-Seite und damit oft zu spaet.
      Cast.watchSoftApDrop(() => dispatch('rotateSoftApInStandby').catch(() => undefined));
      // Das Ergebnis je Flag protokolliert der Plattform-Helfer selbst
      // (Cast/LG.js enableCasting) - hier noch einmal ein Sammelwert zu melden
      // hat genau den Fehlschlag verdeckt, um den es ging.
      return Cast.enableCasting().catch(() => false);
    },
    ensureHotspot() {
      return Cast.ensureSoftApUp()
        .then(() => Cast.softApNotSending())
        .then((down) => {
          if (down === true) return false;
          if (down === false) return true;
          return null;
        })
        .catch(() => null);
    },
    // Weist einen zwischenzeitlichen Power-Off am Zustand des SoftAP nach.
    //
    // Auf LG gibt es keinen PowerStateListener (keyHandler/LG.js ist dort
    // auskommentiert), und visibilitychange feuert beim EINSCHLAFEN nicht auf
    // jeder Firmware - prepareForStandby lief dort also womoeglich nie,
    // clearPending blieb leer, und das SoftAP-Passwort wurde nie neu vergeben.
    // Genau das war am Geraet zu sehen: nach Power Off/On stand immer dasselbe
    // Passwort da.
    //
    // Der Nachweis kommt deshalb im NACHHINEIN: soft_ap faellt laut HCAP-Doku
    // "in power-off sequence" auf 0 und ueberlebt weder Standby noch Reboot.
    // Steht die Property auf 0, obwohl wir den Hotspot fuer den Gast
    // eingeschaltet hatten, war der TV aus.
    //
    // Das ist genauer als jede Heuristik ueber den Vordergrund: kommt der Gast
    // nur aus YouTube oder aus einer per Fernbedienung selbst gestarteten App
    // zurueck, laeuft der Hotspot weiter und es wird nichts geloescht.
    noteStandbyIfSoftApDown({ commit, state }) {
      if (!state.softApActive) return Promise.resolve(false);
      // KEIN frueher Ausstieg mehr bei schon offenem clearPending.
      //
      // Der stand hier, solange clearPending nur ein Ja/Nein war. Seit es
      // zusaetzlich softApRotated gibt, war er ein Fehler: die Standby-Marken
      // gelten GENAU EINMAL (takeStandbyMark/takeSoftApDownMark holen sie ab
      // und loeschen sie), und ein Ausstieg davor liess sie liegen. Vor allem
      // aber blieb softApRotated auf true stehen - ein zweiter Standby
      // bekam also keine neue Rotation mehr, und das Passwort blieb, wie es
      // war. Am Geraet genau so aufgetreten, direkt nachdem die Mehrfach-
      // Rotation behoben war.
      //
      // Jetzt werden die Marken immer zuerst abgeholt; ein Nachweis setzt die
      // Rotation ausdruecklich wieder auf "steht aus".
      // Zweiter Beweis neben soft_ap: eine GEMESSENE Bildschirmabschaltung.
      //
      // Seit prepareForStandby auf LG nichts mehr vormerkt (dort ist 'hidden'
      // mehrdeutig), traegt dieser Pfad die ganze Entscheidung - und soft_ap
      // allein ist wacklig: faehrt die Firmware den Hotspot beim Einschalten
      // von selbst wieder hoch, steht die Property beim Nachsehen schon auf 1
      // und der Beweis waere weg. Die Sonde in helpers/LgScreen haelt dagegen
      // fest, DASS der Schirm aus war (power_mode_changed bzw. isVideoMute),
      // und gibt die Tatsache genau einmal heraus.
      const sawStandby = DeviceType.isLG && LgScreen.takeStandbyMark();
      if (sawStandby) {
        console.log('[cast] Bildschirm war nachweislich aus - Aufraeumen faellig');
        // setClearPending(true) nimmt softApRotated mit zurueck - auch wenn
        // clearPending schon stand. Genau darauf kommt es an: ein neuer
        // Gastwechsel braucht ein neues Passwort.
        commit('setClearPending', true);
        return Promise.resolve(true);
      }
      // Dritter Beweis, und auf dem 43UK660H der einzige, der traegt: der
      // Beobachter hat gesehen, wie soft_ap VON SELBST auf 0 fiel. Das
      // Nachsehen unten kommt dort zu spaet - die Firmware faehrt den Hotspot
      // beim Einschalten selbst wieder hoch (siehe Cast/LG.js watchSoftApDrop).
      if (Cast.takeSoftApDownMark()) {
        console.log('[cast] soft_ap war zwischenzeitlich auf 0 - der TV war aus, Aufraeumen faellig');
        // Hat die Standby-Rotation einen Schluessel hinterlegt, gilt er ab
        // JETZT: die Firmware hat den Hotspot beim Einschalten mit ihm
        // hochgefahren (Uebergang soft_ap 0 -> 1). Ab hier ist er also der
        // Schluessel, den die Cast-Seite anzeigen und der QR tragen muss.
        // REIHENFOLGE: erst clearPending, dann softApRotated.
        //
        // setClearPending(true) setzt softApRotated zurueck - ein neuer
        // Gastwechsel braucht ja ein neues Passwort. Umgekehrt herum wuerde es
        // die Rotation entwerten, die im Standby schon gelaufen ist, und die
        // Cast-Seite holte sie gleich noch einmal nach.
        commit('setClearPending', true);
        const staged = readStagedKey();
        if (staged) {
          rememberKey(staged);
          writeStagedKey(null);
          commit('setSoftAP', { key: staged });
          softApRotatedInStandby = true;
          // Der Standby-Weg IST die Rotation fuer diesen Gastwechsel.
          commit('setSoftApRotated', true);
          console.log('[cast] Im Standby vergebenes SoftAP-Passwort gilt ab jetzt');
        }
        return Promise.resolve(true);
      }
      return Cast.softApIsDown().then((down) => {
        if (down !== true) {
          console.log('[cast] Kein Standby nachweisbar (soft_ap nicht unten) - nichts aufzuraeumen');
          return false;
        }
        console.log('[cast] SoftAP ist unten - der TV war aus, Aufraeumen faellig');
        commit('setClearPending', true);
        return true;
      });
    },
    // Vermerkt, dass die App den Vordergrund an eine FREMDE App abgibt.
    //
    // launchApp und launchCastEntry setzen das selbst. Fuer alles andere, was
    // eine Geraete-App startet - der Browser aus navigation/openInBrowser -,
    // gibt es diese Aktion: ohne den Vermerk liest der LG-Standby-Haken den
    // Wechsel in den Hintergrund als Ausschalten und raeumt die Cast-Daten des
    // Gastes mitten in seiner Sitzung auf.
    noteAppHandoff() {
      appHandoff = true;
      ForeignApp.noteOwnLaunch();
      // Eine Fremd-App laeuft im Vordergrund, der TV ist also an.
      cancelStandbyReboot('Fremd-App gestartet');
      return true;
    },
    // ---- Ausschalten -----------------------------------------------------
    // Alles, was hier laeuft, verzoegert das Dunkelwerden des Bildschirms.
    // Deshalb laeuft hier nur noch das Billigste:
    //
    //   1. Loeschen beim naechsten Neustart scharf schalten (Samsung
    //      deleteAppPrivateInfo("ON") - synchron, Millisekunden).
    //   2. Den SoftAP zumachen - EIN Aufruf, kein Neustart.
    //   3. Merken, dass noch aufzuraeumen ist.
    //
    // Das eigentliche Loeschen (deleteAppPrivateInfoNow bzw. requestCheckout)
    // und das neue SoftAP-Passwort macht der Wake-Pfad. Vorher stand beides
    // hier - mit synchronem SoftAP-Neustart auf Samsung und vier hcap-Aufrufen
    // a 5 s Timeout auf LG, in dreifacher Ausfuehrung: bis zu 18 s, bis der TV
    // wirklich ausging.
    //
    // Am Versprechen aendert das nichts. sessionActive und softApActive fallen
    // weiterhin erst, wenn das Loeschen bestaetigt ist, clearPending haelt die
    // offene Arbeit ueber Standby UND Kaltstart hinweg fest, und clearGate
    // laesst vorher keine OTT-App starten.
    //
    // source 'hidden' heisst "nur visibilitychange gesehen, kein bestaetigter
    // Standby" - dann wird der Start einer eigenen App nicht als Standby
    // missverstanden (LG, siehe appHandoff).
    prepareForStandby({ commit, state, dispatch }, source) {
      // clearRun wird hier BEWUSST nicht zurueckgesetzt: laeuft noch ein Lauf
      // vom letzten Aufwachen, muss clearGate weiter darauf warten koennen.
      // Freigegeben wird er, wenn er fertig ist (siehe unten).
      if (standbyRun) return standbyRun;
      // Ein Cast-Start VOM HANDY hinterliess bisher keine Spur.
      //
      // sessionActive setzten nur launchApp (unsere Kachel) und launchCastEntry
      // (blaue Taste). Waehlt der Gast den TV dagegen in der YouTube-App auf
      // seinem Handy - der Normalfall -, holt der TV die App selbst in den
      // Vordergrund, und die App erfuhr davon nichts. Folgen am Geraet
      // beobachtet: der Hinweis auf die rote Taste fehlte, obwohl gerade ein
      // Video lief, und scheduleStandbyReboot stieg bei !sessionActive sofort
      // aus - es gab nach aussen keine Cast-Nutzung, die aufzuraeumen waere.
      //
      // Das Kennzeichen ist eindeutig: Die App verliert den Vordergrund, OHNE
      // dass wir selbst etwas gestartet haben (appHandoff), waehrend ein Handy
      // am SoftAP haengt. Dann war es der TV - also ein Cast-Start.
      //
      // Nur der Merker wird gesetzt. Am Standby-Zweig darunter aendert sich
      // nichts: ob dieses 'hidden' ein App-Start oder ein Ausschalten war, ist
      // damit weiterhin nicht entschieden - aufzuraeumen gibt es in beiden
      // Faellen etwas.
      // Der Nachweis unterscheidet sich je Plattform: wo Clients am SoftAP
      // gemeldet werden (Tizen), zaehlt die Messung. Wo nicht (LG - siehe
      // Cast/index.js hasSoftAPClientInfoListener), zaehlt der eingerichtete
      // Hotspot als Indiz. Sonst bliebe die haeufigste Nutzung ueberhaupt -
      // Cast vom Handy - dauerhaft unsichtbar.
      //
      // "Wird gemeldet" heisst: ein Listener hat in diesem Lauf wirklich etwas
      // geliefert. Dass die Funktion existiert, reicht nicht - am U800F gibt es
      // sie, sie bleibt aber stumm (siehe softApEventSeen).
      const clientsReported = Cast.hasSoftAPClientInfoListener() && softApEventSeen;
      const guestPresent = clientsReported
        ? !!(state.pairing && state.pairing.connected)
        : !!state.softApActive;
      if (source === 'hidden' && !appHandoff && !state.sessionActive && guestPresent) {
        console.log('[cast] Vordergrund an eine Cast-App abgegeben - Sitzung vermerkt');
        commit('setSessionActive', true);
      }
      if (source === 'hidden' && appHandoff) {
        console.log('[cast] Hintergrund durch eigenen App-Start - kein Standby');
        return Promise.resolve(false);
      }
      if (!state.sessionActive && !state.softApActive) return Promise.resolve(false);
      // clearPending NUR, wenn der Standby wirklich feststeht.
      //
      // Auf LG ist 'hidden' mehrdeutig: es steht genauso fuer eine Fremd-App im
      // Vordergrund - und genau so laeuft Cast vom Handy. Wuerde hier
      // vorgemerkt, loeschte clearGuestCredentials beim Zurueckkommen die
      // Anmeldung, die der Gast gerade benutzt. Dass das bisher niemandem
      // auffiel, lag allein daran, dass ohne Checkout-Snapshot ohnehin nichts
      // geloescht wurde; seit der Snapshot steht, wirkt es.
      //
      // Der Beweis kommt statt dessen beim AUFWACHEN nach
      // (noteStandbyIfSoftApDown): soft_ap == 0 oder eine gemessene
      // Bildschirmabschaltung. Auf Tizen bleibt es beim bisherigen Weg - dort
      // ruft ein echter Power-Listener diese Aktion, nicht nur visibilitychange.
      const standbyUnproven = DeviceType.isLG && source === 'hidden' && !appHandoff;
      if (standbyUnproven) {
        console.log(
          '[cast] Hintergrund auf LG - Standby noch nicht bewiesen, Aufraeumen wird beim Aufwachen entschieden'
        );
      } else {
        commit('setClearPending', true);
      }
      // Der Notnagel zuerst, weil er nichts kostet: greift der Wake-Pfad nicht
      // (Firmware, Stromausfall), loescht der naechste echte Neustart.
      try {
        Cast.armClearCredentials();
      } catch (e) {
        console.log('[cast] armClearCredentials (Standby) fehlgeschlagen:', e);
      }
      // Der Hotspot wird hier NICHT mehr abgeschaltet.
      //
      // Der Gedanke war: im Standby soll niemand mehr mit dem alten Passwort
      // hereinkommen, eingeschaltet wird beim Aufwachen. Die Voraussetzung
      // "wir koennen ihn wieder einschalten" hat sich am Geraet als falsch
      // erwiesen - enableSoftAP scheitert dort mit "Failed to enable Soft AP",
      // und der Hotspot blieb ueber mehrere Power-Zyklen aus. Ein toter Hotspot,
      // der nur ueber das Service-Menue zurueckkommt, ist ungleich schlimmer als
      // ein Hotspot, der im Standby noch kurz mit dem alten Schluessel sendet -
      // zumal das Passwort beim Aufwachen ohnehin neu vergeben wird.
      //
      // Es bleibt beim Scharfschalten oben: ein Geraete-Aufruf, Millisekunden.
      dispatch('scheduleStandbyReboot');
      standbyRun = withBudget(Promise.resolve(true), STANDBY_BUDGET_MS, 'prepareForStandby');
      return standbyRun;
    },
    // Plant den Neustart, der die Anmeldedaten des Gastes wirklich entfernt.
    //
    // Nur wenn beides zutrifft:
    //   - der Gast hat eine OTT-App benutzt (sessionActive), und
    //   - dieses Geraet kann NICHT sofort loeschen. Kann es das, hat der
    //     Wake-Pfad es schon getan und ein Neustart waere sinnlose Unruhe.
    //
    // Beim Aufwachen und beim Start einer Fremd-App wird der Plan verworfen -
    // laeuft die Uhr also ab, war der TV die ganze Zeit aus.
    scheduleStandbyReboot({ state, dispatch, getters }) {
      cancelStandbyReboot('neu geplant');
      rebootCommitted = false;
      if (!STANDBY_REBOOT_MIN) return false;
      // Ein Neustart ist ein grober Eingriff. Er passiert nur, wenn es
      // wirklich etwas zu loeschen gibt - und jede dieser Bedingungen hat einen
      // eigenen Grund:
      //
      // 1. Cast wurde benutzt. sessionActive setzt nur ein tatsaechlich
      //    gestarteter App-Einstieg, und ein fehlgeschlagener Start nimmt ihn
      //    wieder zurueck (siehe launchApp).
      if (!state.sessionActive) return false;
      // 2. Das Geraet kann Cast ueberhaupt. Ohne das kann keine Anmeldung
      //    entstanden sein, und ein alter Merker aus einer fremden Sitzung darf
      //    keinen Neustart ausloesen.
      if (!getters.isSupported) {
        console.log('[cast] Kein Standby-Neustart - dieses Geraet unterstuetzt Cast nicht');
        return false;
      }
      // 3. Es ist ueberhaupt eine Cast-App eingerichtet. Ist die Liste leer,
      //    gibt es nichts, worin sich ein Gast anmelden koennte.
      if (!state.apps || !state.apps.length) {
        console.log('[cast] Kein Standby-Neustart - keine Cast-App eingerichtet');
        return false;
      }
      // 4. Es gibt ueberhaupt etwas, das NUR ein Neustart erledigen kann:
      //    - Anmeldedaten, wenn das Geraet nicht sofort loeschen kann, oder
      //    - das SoftAP-Passwort, wenn das Geraet seinen Hotspot nicht neu
      //      starten kann (dann schreibt stageSoftApRotation den neuen
      //      Schluessel unmittelbar vor dem Neustart).
      //    Kann das Geraet beides selbst, ist ein Neustart sinnlose Unruhe.
      const credsNeedReboot = !Cast.hasClearCredentialsNow();
      const softApNeedsReboot = state.softApActive && !Cast.canRestartSoftAp();
      if (!credsNeedReboot && !softApNeedsReboot) {
        console.log('[cast] Kein Standby-Neustart noetig - dieses Geraet erledigt beides selbst');
        return false;
      }
      const ms = STANDBY_REBOOT_MIN * 60 * 1000;
      console.log('[cast] Standby-Neustart in ' + STANDBY_REBOOT_MIN + ' min geplant');
      // Den Merker "dieser Neustart dient dem Loeschen" JETZT schreiben, nicht
      // erst in der Sekunde vor dem Neustart.
      //
      // Gemessen am U800F (Tizen 9, 30.09.2026): kurz vor rebootDevice
      // geschrieben, fehlte er nach dem Hochfahren - Chromium bringt
      // LocalStorage nur verzoegert und gebuendelt auf die Platte, und die
      // App schreibt dort laufend. sessionActive fiel so nie, und JEDER
      // weitere Standby startete den TV neu, auch ganz ohne Cast.
      // Beim Planen ist bis zum Neustart eine volle Wartezeit Zeit dafuer;
      // wird der Plan verworfen (Aufwachen, Fremd-App), nimmt
      // cancelStandbyReboot den Merker wieder weg.
      writeRebootClearFlag(true);
      standbyRebootTimer = setTimeout(() => {
        standbyRebootTimer = null;
        // Letzte Kontrolle: die App muss noch im Hintergrund sein. Ist sie
        // sichtbar, ist der TV an - dann wird auf keinen Fall neu gestartet.
        if ((typeof document !== 'undefined' && !document.hidden) || tvIsOn()) {
          console.log('[cast] Standby-Neustart verworfen - die App ist im Vordergrund bzw. der TV ist an');
          // Kein Neustart - der beim Planen gesetzte Merker muss weg.
          writeRebootClearFlag(false);
          return;
        }
        console.log('[cast] Standby-Neustart jetzt - Anmeldedaten werden beim Hochlaufen geloescht');
        // Vermerken, DASS dieser Neustart dem Loeschen diente. Ohne diesen
        // Merker faellt sessionActive auf Geraeten ohne Sofort-Loeschen nie
        // wieder - eine einzige Cast-Nutzung wuerde den TV dann nach JEDEM
        // Standby neu starten, fuer immer. Ausgewertet beim naechsten App-Start
        // (settleAfterRebootClear).
        writeRebootClearFlag(true);
        // Und den Passwortwechsel vorbereiten, den erst dieser Neustart
        // wirksam macht. Kann das Geraet seinen Hotspot selbst neu starten,
        // ist das ein No-op.
        dispatch('stageSoftApRotation');
        // Das Loeschen ist scharf geschaltet (deleteAppPrivateInfo "ON"), die
        // Firmware erledigt es waehrend des Starts.
        //
        // Kurz warten: die Merker sind eben geschrieben worden. localStorage
        // meldet den Schreibzugriff synchron zurueck, bringt ihn aber nicht
        // zwingend sofort auf die Platte - ein Neustart unmittelbar danach kann
        // ihn verlieren.
        let wait = REBOOT_FLUSH_MS;
        if (REBOOT_FRONT) {
          // VOR dem launch: startet er die App neu, findet der neue Code den
          // Merker und holt den Neustart nach (siehe REBOOT_PENDING_KEY).
          writeRebootPending(true);
          rebootCommitted = true;
          const launched = BringToFront.forReboot('vor dem Standby-Neustart');
          console.log('[cast] tv-app vor dem Neustart nach vorn geholt: ' + launched);
          if (launched) wait = Math.max(wait, REBOOT_FRONT_SETTLE_MS);
        }
        // Auch diese Wartezeit gehoert zum Plan: wacht der TV jetzt auf,
        // bricht cancelStandbyReboot sie ab.
        standbyRebootTimer = setTimeout(() => {
          standbyRebootTimer = null;
          // Die Seite lebt noch - der launch hat sie nicht neu gestartet.
          writeRebootPending(false);
          if (tvIsOn()) {
            console.log('[cast] Standby-Neustart verworfen - der TV wurde eben eingeschaltet');
            writeRebootClearFlag(false);
            return;
          }
          // Startet die Firmware die App nach dem Neustart nicht von selbst,
          // weckt sie dieser Auftrag (siehe helpers/WakeAlarm).
          if (REBOOT_FRONT) WakeAlarm.set('Standby-Neustart');
          rebootCommitted = true;
          dispatch('device/reboot', null, { root: true }).then((r) => {
            if (!r || r.ok !== false) return;
            dispatch('rollbackStandbyReboot', r);
          });
        }, wait);
      }, ms);
      return true;
    },
    // Der Standby-Neustart ist gescheitert (am BU800: rebootDevice ->
    // SecurityError). Alles zuruecknehmen, was erst der Neustart wirksam
    // gemacht haette - sonst stuende auf dem TV ein neues SoftAP-Passwort, das
    // der Hotspot nicht sendet, die Cast-Seite zeigte es aber an (configure-
    // SoftAP liest es auf nicht neustartbaren Geraeten vom TV zurueck), und
    // der Gast kaeme nicht ins Netz.
    rollbackStandbyReboot(ctx, result) {
      console.log('[cast] Standby-Neustart gescheitert (' + ((result && result.detail) || '?') + ') - wird zurueckgenommen');
      rebootCommitted = false;
      writeRebootClearFlag(false);
      WakeAlarm.clear('Neustart gescheitert');
      const staged = readStagedKey();
      if (!staged) return Promise.resolve(false);
      writeStagedKey(null);
      if (!sessionSoftApKey) {
        console.log('[cast] Kein gemerktes SoftAP-Passwort - der vorbereitete Schluessel bleibt auf dem TV');
        return Promise.resolve(false);
      }
      return Cast.stageSoftApKey(sessionSoftApKey).then((ok) => {
        console.log('[cast] Altes SoftAP-Passwort zurueckgeschrieben: ' + ok);
        return ok;
      });
    },
    // ---- Aufwachen -------------------------------------------------------
    // Hier ist Zeit: der TV ist an, die App zeigt ihre Startseite, und niemand
    // wartet auf einen dunklen Bildschirm.
    //
    // Laeuft NUR nach einem bestaetigten Standby (clearPending). Ohne diesen
    // Filter wuerde jede Rueckkehr aus einer Fremd-App den Gast mitten in
    // seiner Sitzung abmelden - und ein blosser App-Reload waere fatal, denn
    // genau dafuer sind die Merker persistiert.
    clearGuestCredentials({ commit, state, dispatch }) {
      standbyRun = null;
      appHandoff = false;
      // Der TV ist an - der geplante Standby-Neustart ist damit vom Tisch.
      //
      // ABER nicht, wenn der TV nachweislich noch im Standby steht: beim
      // App-Start ruft main.js (resumePendingCastClear) erst
      // settleAfterRebootClear und gleich danach diese Action auf. Startete
      // BringToFront die App im Standby neu, hat settleAfterRebootClear eben
      // den nachgeholten Neustart geplant - und der wurde hier sofort wieder
      // abgebrochen (U800F, 21.10.2026: kein Neustart, Gastdaten blieben).
      if (standbyRebootTimer && tvIsInStandby()) {
        console.log('[cast] Geplanter Neustart bleibt - der TV ist noch im Standby');
      } else {
        cancelStandbyReboot('Geraet ist wieder an');
      }
      // Dreifachausloesung beim Aufwachen wie beim Ausschalten: ein Lauf reicht.
      if (fullRun) return clearRun || fullRun;
      if (!state.clearPending) return Promise.resolve(false);
      if (!state.sessionActive && !state.softApActive) {
        commit('setClearPending', false);
        return Promise.resolve(false);
      }
      // Vor dem Sofort-Loeschen fuer den Neustart scharf schalten: greift die
      // Sofort-Variante nicht, holt es der naechste echte Neustart nach.
      try {
        Cast.armClearCredentials();
      } catch (e) {
        console.log('[cast] armClearCredentials fehlgeschlagen:', e);
      }
      // Gastwechsel: auch die Daten des Google-Cast-Dienstes loeschen
      // (Samsung googlecast.clearData; nur der Dienst, nicht die tv-app).
      // Nicht abgewartet - das Tor haengt an den Anmeldedaten.
      Cast.clearCastData().catch(() => false);

      // ---- Teil 1: Anmeldedaten. Schnell, und HIERAN haengt das Tor. -------
      //
      // Kennt die Firmware kein deleteAppPrivateInfoNow (am U800F/Tizen 9.0 ist
      // das so), ist sofort nichts zu loeschen. Dann bleibt es beim scharf
      // geschalteten Loeschen zum naechsten Neustart - und der Merker darf
      // FALLEN, sonst blockiert das Tor bei jedem App-Start erneut und der Gast
      // wartet jedes Mal auf einen Lauf, der nichts erreichen kann.
      const creds = (function () {
        if (!state.sessionActive) return Promise.resolve(true);
        if (!Cast.hasClearCredentialsNow()) {
          console.log(
            '[cast] Kein Sofort-Loeschen auf diesem Geraet - die Anmeldedaten ' +
              'verschwinden erst mit dem naechsten Neustart (deleteAppPrivateInfo ist scharf).'
          );
          return Promise.resolve(false);
        }
        return Cast.clearCredentialsNow()
          .then((r) => {
            console.log('[cast] Anmeldedaten geloescht:', r);
            commit('setSessionActive', false);
            // Der Checkout setzt den TV auf den Snapshot zurueck - moeglicher-
            // weise samt abgeschaltetem DIAL. Danach faende das Handy den
            // Fernseher nicht mehr, obwohl der Hotspot laeuft.
            dispatch('ensureCastFlags').catch(() => undefined);
            return true;
          })
          .catch((err) => {
            // Merker bewusst STEHEN LASSEN: beim naechsten Ein-/Ausschalten
            // wird es erneut versucht.
            console.log('[cast] Sofort-Loeschen nicht moeglich:', errText(err));
            return false;
          });
      })();
      clearRun = creds;
      const releaseCreds = () => {
        if (clearRun === creds) clearRun = null;
      };
      creds.then(releaseCreds, releaseCreds);

      // ---- Teil 2: das SoftAP-Passwort - NICHT HIER. ----------------------
      //
      // Es wird beim Oeffnen der Cast-Seite erneuert, nicht beim Aufwachen.
      //
      // Der Grund ist hart: disableSoftAP und enableSoftAP sind SYNCHRONE
      // Firmware-Aufrufe und brauchen auf diesen Geraeten je rund zehn Sekunden.
      // Waehrend dieser Zeit steht der JS-Thread - die App zeichnet nicht,
      // reagiert auf keine Taste, und die Notfall-Tastenfolge in Home.vue kommt
      // nicht durch. Genau so ist ein BU800 auf der Home-Seite eingefroren:
      // sechs Sekunden nach dem Start lief die Rotation los und blockierte den
      // Thread, bis die Firmware den TV selbst neu startete.
      //
      // Auf der Cast-Seite ist derselbe Aufwand vertretbar: dort steht ohnehin
      // "Verbindung wird vorbereitet", der Gast erwartet einen Moment, und das
      // neue Passwort wird genau dort gebraucht. Ausgeloest wird es von
      // configurePairing, solange clearPending UND softApActive stehen - also
      // nach einem Standby und nur einmal.
      const run = creds.then(() => {
        dispatch('settleClearPending');
        return !state.clearPending;
      });
      fullRun = run;
      const releaseFull = () => {
        if (fullRun === run) fullRun = null;
      };
      run.then(releaseFull, releaseFull);
      return creds;
    },
    // Der Gast loescht seine Anmeldedaten SELBST (rote Taste auf der
    // Cast-Seite) - fuer alle, die nicht darauf vertrauen wollen, dass das
    // Ausschalten es erledigt, und fuer den Gast, der das Zimmer verlaesst und
    // sein Konto nicht auf dem Fernseher stehen lassen will.
    //
    // Unterschied zum Ausschaltpfad: hier steht jemand davor und wartet auf
    // eine Antwort. Deshalb wird SOFORT geloescht, wenn die Firmware das kann.
    // Kann sie es nicht (U800F/Tizen 9.0 kennt nur deleteAppPrivateInfo("ON")),
    // bleibt es beim scharf geschalteten Loeschen - dann sagt die Rueckgabe,
    // dass nur ein Neustart es wirklich entfernt, und die Seite fragt danach.
    //
    // Der SoftAP wird hier bewusst NICHT angefasst: sein Passwort gehoert dem
    // Zimmer und nicht dem Gast, und ein Neustart des Hotspots wuerde genau das
    // Handy hinauswerfen, das gerade verbunden ist. Neu vergeben wird es beim
    // Gastwechsel (rotateSoftAP).
    //
    // Rueckgabe: { deleted, needsReboot, detail }
    // Nimmt den Checkout-Snapshot - den Zustand, auf den requestCheckout den TV
    // spaeter zurueckSETZT. Ohne ihn loescht der Checkout auf LG nichts.
    //
    // DER ZEITPUNKT IST DAS GANZE THEMA. Der Snapshot friert ein, worauf
    // zurueckgesetzt wird: waere gerade ein Gast in YouTube angemeldet, wuerde
    // GENAU DIESE Anmeldung ab dann bei jedem Checkout wiederhergestellt - das
    // Gegenteil des Gewollten, und schlimmer als der jetzige Zustand.
    //
    // Deshalb laeuft er nur, wenn nichts auf einen Gast hindeutet:
    //   - keine laufende Cast-Sitzung (sessionActive)
    //   - kein offenes Aufraeumen (clearPending)
    //   - kein Gast am Hotspot (softApActive)
    // Das ist der Zustand direkt nach Installation, Reset oder einem erledigten
    // Gastwechsel. Mit force=true laesst sich das uebergehen - dafuer ist der
    // Admin-Befehl da, wenn jemand am Geraet steht und es besser weiss.
    //
    // Genommen wird EINMAL pro Geraet (persistierter Merker). Ein zweiter
    // Snapshot wuerde den ersten ueberschreiben - und damit womoeglich einen
    // sauberen Stand durch einen benutzten ersetzen.
    takeCheckoutSnapshot({ commit, state }, options) {
      const opts = options || {};
      const force = opts.force === true;
      if (!Cast.needsCheckoutSnapshot()) {
        const detail = 'Plattform kennt keinen Checkout-Snapshot';
        commit('setSnapshot', { taken: false, reason: detail });
        return Promise.resolve({ ok: false, taken: false, detail: detail });
      }
      if (readSnapshotFlag() && !force) {
        commit('setSnapshot', { taken: true, reason: '' });
        return Promise.resolve({ ok: true, taken: false, detail: 'Snapshot liegt bereits vor' });
      }
      const dirty = [];
      if (state.sessionActive) dirty.push('Cast-Sitzung laeuft');
      if (state.clearPending) dirty.push('Aufraeumen offen');
      if (state.softApActive) dirty.push('Gast am Hotspot');
      if (dirty.length && !force) {
        // Der haeufigste Fall auf einem Geraet, das schon einmal benutzt wurde:
        // softApActive ueberdauert als persistierter Merker jeden Neustart und
        // faellt erst beim abgeschlossenen Gastwechsel. Von selbst laeuft der
        // Snapshot dann nie mehr - der Admin-Befehl mit force ist dafuer da,
        // und dieser Grund steht ab jetzt auch in der Zustandsmeldung.
        const detail = 'kein Snapshot - das Geraet ist nicht nachweislich sauber (' + dirty.join(', ') + ')';
        console.log('[cast] ' + detail);
        commit('setSnapshot', { taken: false, reason: dirty.join(', ') });
        return Promise.resolve({ ok: false, taken: false, detail: detail });
      }
      if (dirty.length) {
        console.log('[cast] Checkout-Snapshot ERZWUNGEN, obwohl: ' + dirty.join(', '));
      }
      return Cast.takeCheckoutSnapshot()
        .then(() => {
          writeSnapshotFlag(true);
          Cast.noteCheckoutSnapshot(true);
          commit('setSnapshot', { taken: true, reason: force && dirty.length ? 'erzwungen: ' + dirty.join(', ') : '' });
          console.log('[cast] Checkout-Snapshot genommen - ab jetzt loescht der Checkout wirklich');
          return { ok: true, taken: true, detail: 'Snapshot genommen' };
        })
        .catch((err) => {
          const detail = errText(err);
          console.log('[cast] Checkout-Snapshot fehlgeschlagen:', detail);
          commit('setSnapshot', { taken: false, reason: detail });
          return { ok: false, taken: false, detail: detail };
        });
    },
    clearGuestDataNow({ commit, dispatch }) {
      // Der Notnagel zuerst, er kostet nichts: greift das Sofort-Loeschen
      // nicht, holt es der naechste Neustart nach - auch der beim Ausschalten.
      dispatch('armClearCredentials');
      // Google Cast hat eigene Gastdaten (Samsung: googlecast.clearData) -
      // die gehen sofort, unabhaengig vom Neustart.
      Cast.clearCastData().catch(() => false);
      if (!Cast.hasClearCredentialsNow()) {
        console.log('[cast] Loeschen auf Wunsch des Gastes: nur scharf geschaltet, Neustart noetig');
        return Promise.resolve({
          deleted: false,
          needsReboot: true,
          detail: 'kein Sofort-Loeschen auf diesem Geraet',
        });
      }
      return Cast.clearCredentialsNow()
        .then((r) => {
          // Ein gelungener Aufruf ist nicht ueberall ein Nachweis: auf LG
          // quittiert requestCheckout auch dann mit result:true, wenn es mangels
          // Snapshot gar nichts zurueckzusetzen gibt (siehe Cast/LG.js).
          //
          // Der Vorbehalt gehoert zum Betreiber (Log und Socket-Rueckmeldung),
          // nicht in die Gastoberflaeche.
          //
          // Der NEUSTART dagegen schon: am 43UK660H waren die YouTube-Konten
          // nach der roten Taste noch da, nach einem Power Off/On aber weg -
          // bei demselben Aufruf. Der Checkout raeumt also, die laufende
          // Firmware zeigt den geraeumten Stand nur erst nach dem Neustart
          // (so steht es auch in der HCAP-Doku zu requestCheckout). Wer
          // ausschaltet, liefert ihn beilaeufig mit; wer die rote Taste
          // drueckt, muss gefragt werden.
          //
          // Eine fruehere Fassung hier hat needsReboot bewusst auf false
          // gelassen - unter der Annahme, ein Neustart wuerde auf LG ohnehin
          // nichts loeschen. Die Beobachtung am Geraet widerlegt das.
          const verified = Cast.isClearCredentialsVerified();
          const needsReboot = Cast.clearTakesEffectOnReboot();
          console.log(
            '[cast] Loeschen auf Wunsch des Gastes: Aufruf ok' +
            (verified === true
              ? ' und auf dieser Plattform belastbar'
              : verified === null
                ? ' - Wirkung auf diesem Geraet nicht geprueft'
                : ' - WIRKUNG AUF DIESEM GERAET NICHT NACHWEISBAR') +
            ':',
            r
          );
          commit('setSessionActive', false);
          dispatch('settleClearPending');
          // Siehe clearGuestCredentials: der Checkout kann DIAL mit
          // zuruecksetzen, und ohne das findet kein Handy den TV mehr.
          dispatch('ensureCastFlags').catch(() => undefined);
          return {
            deleted: true,
            needsReboot: needsReboot,
            // Fuer die Socket-Rueckmeldung und die Info-Seite: sagt, ob hinter
            // dem "erledigt" ein Nachweis steht oder nur eine Quittung.
            verified: verified,
            detail: String(r || 'ok'),
          };
        })
        .catch((err) => {
          const detail = errText(err);
          console.log('[cast] Loeschen auf Wunsch des Gastes fehlgeschlagen:', detail);
          return { deleted: false, needsReboot: true, detail: detail };
        });
    },
    // Der Neustart, der ein manuelles Loeschen wirksam macht.
    //
    // Gleicher Ablauf wie beim Standby-Neustart, nur sofort und auf Wunsch:
    // Merker setzen, damit settleAfterRebootClear den Vorgang nach dem
    // Hochlaufen abschliesst, den Passwortwechsel vorbereiten (auf Geraeten,
    // die ihren Hotspot nicht neu starten koennen), dann neu starten.
    //
    // Ein bereits geplanter Standby-Neustart ist damit erledigt - der TV startet
    // ja jetzt.
    rebootForClear({ dispatch }) {
      dispatch('armClearCredentials');
      cancelStandbyReboot('Neustart auf Wunsch des Gastes');
      writeRebootClearFlag(true);
      dispatch('stageSoftApRotation');
      return new Promise((resolve) => {
        // Kurz warten wie im Standby-Pfad: die Merker sind eben in den
        // localStorage geschrieben worden und muessen den Neustart ueberleben.
        setTimeout(() => {
          dispatch('device/reboot', null, { root: true });
          resolve(true);
        }, REBOOT_FLUSH_MS);
      });
    },
    // Loescht den Merker clearPending, wenn nichts mehr aussteht.
    //
    // "Aussteht" heisst genau zweierlei:
    //   - softApActive: das SoftAP-Passwort ist noch das des letzten Gastes.
    //     Erneuert wird es beim Oeffnen der Cast-Seite (siehe configurePairing).
    //   - sessionActive UND das Geraet kann sofort loeschen: dann ist das
    //     Loeschen faellig und nicht erledigt. Kann es das nicht, ist mit dem
    //     scharf geschalteten deleteAppPrivateInfo alles getan, was moeglich
    //     ist - dann darf der Merker fallen, sonst blockiert das Tor bei jedem
    //     App-Start erneut.
    //
    // Von beiden Pfaden aufgerufen, damit die Bedingung an EINER Stelle steht.
    settleClearPending({ commit, state }) {
      if (!state.clearPending) return true;
      // softApActive allein heisst NICHT "noch zu erledigen".
      //
      // Der Merker sagt nur, dass der Hotspot fuer einen Gast eingerichtet ist -
      // und das bleibt er auch nach der Rotation, dann eben fuer den neuen. Als
      // "offen" gelesen, fiel clearPending nie, und configurePairing rotierte
      // bei JEDEM Oeffnen der Cast-Seite erneut. Am Geraet gemessen: drei
      // Passwoerter in vier Sekunden Abstand, und jeder Wechsel wirft das Handy
      // des Gastes aus dem Netz.
      //
      // Offen ist der SoftAP-Teil also nur, solange fuer DIESEN Gastwechsel
      // noch nicht rotiert wurde.
      const softApOpen = state.softApActive && !state.softApRotated;
      const credsOpen = state.sessionActive && Cast.hasClearCredentialsNow();
      if (softApOpen || credsOpen) {
        console.log(
          '[cast] Aufraeumen noch offen (SoftAP: ' + softApOpen + ', Anmeldedaten: ' + credsOpen + ')'
        );
        return false;
      }
      commit('setClearPending', false);
      return true;
    },
    // Das Tor vor dem Start einer OTT-App bzw. vor der Uebergabe an das native
    // Cast-Overlay.
    //
    // Es traegt die Sicherheitsgarantie, seit das Loeschen nicht mehr im
    // Ausschaltpfad steckt: der naechste Gast kann nichts starten, bevor die
    // Daten des Vorgaengers weg sind. Steht noch Arbeit aus, ohne dass ein Lauf
    // laeuft - der Wake-Trigger ist auf dieser Firmware nicht gekommen -, wird
    // sie hier nachgeholt statt uebergangen.
    clearGate({ state, dispatch }) {
      if (clearRun) return clearRun.catch(() => false);
      if (!state.clearPending) return Promise.resolve(true);
      console.log('[cast] Tor: Aufraeumen stand noch aus - wird jetzt nachgeholt');
      return dispatch('clearGuestCredentials').catch(() => false);
    },
    finalizeCheckOut({ commit, dispatch }) {
      Cast.finalizeForCheckOut();
      commit('setPrepared', false);
      commit('setSessionActive', false);
      // Der SoftAP-Listener laeuft ueber die Pairing-Ansicht hinaus weiter -
      // beim Check-out gehoert er weg, sonst zaehlen Geraete des alten Gastes
      // in die Sitzung des neuen hinein.
      dispatch('unwatchSoftAP');
      // Und der SoftAP-Zugang des alten Gastes wird ungueltig. Beim echten
      // Check-out ist das der eigentlich richtige Zeitpunkt dafuer - das
      // Rotieren beim Aus-/Einschalten ist nur der Ersatz, solange kein PMS
      // den Gastwechsel meldet.
      dispatch('rotateSoftAP').catch(() => {});
      return Promise.resolve();
    },
  },
  getters: {
    getApps(state) {
      return state.apps;
    },
    getSelectedApp(state) {
      return state.apps[state.selectedIndex] || null;
    },
    // Haengt absichtlich an state.capabilities: dadurch wird der Getter nach
    // detect() neu berechnet (Cast.isSupported() selbst ist nicht reaktiv).
    isSupported(state) {
      void state.capabilities;
      return Cast.isSupported();
    },
    // Fallback auf die versionsbasierte Strategie, falls der Getter vor init()
    // gelesen wird (state.strategy ist bis dahin null).
    getStrategy(state) {
      return state.strategy || Cast.castStrategy();
    },
    // Liegen Daten eines Gastes auf dem Fernseher, die er loeschen koennen soll?
    //
    // sessionActive ist der harte Nachweis - aber nur dort, wo er entstehen
    // KANN: gesetzt wird er von launchApp (unsere Kachel) und launchCastEntry
    // (blaue Taste). Waehlt der Gast den TV dagegen in der YouTube-App auf
    // seinem Handy, holt der TV die App selbst in den Vordergrund, und die
    // einzige Quelle waere ein Client am SoftAP - die es auf LG nicht gibt
    // (Cast/index.js: "LG hat kein Pendant" zu addSoftAPClientInfoListener).
    //
    // Am Geraet beobachtet: YouTube lief ueber Cast, und der Hinweis auf die
    // rote Taste erschien trotzdem nirgends.
    //
    // Deshalb zaehlt dort, wo sich Clients nicht messen lassen, der eingerichtete
    // SoftAP als Indiz: Wer den Hotspot des Zimmers bekommen hat, hat mit dem TV
    // gecastet. Die Folge eines Fehlurteils ist gering - der Gast sieht ein
    // Loeschangebot fuer eine Anmeldung, die es womoeglich nicht gibt, und ein
    // Checkout ohne Fund setzt schlicht auf den Snapshot zurueck.
    //
    // Gefragt wird, ob der Client-Listener WIRKLICH registriert ist, nicht ob
    // die Funktion existiert: am BU800 (T-NKLBDEUC-1720.7) gibt es
    // addSoftAPClientInfoListener, die Firmware lehnt ihn aber ab ("not
    // implemented in pepper network plugin"). Dann zaehlt der aktive SoftAP
    // wieder als Indiz - sonst war die Rote Taste nach einem reinen
    // Handy-Cast nicht anwaehlbar.
    hasGuestData(state) {
      if (state.sessionActive || state.clearPending) return true;
      return !softApClientListener && !!state.softApActive;
    },
    // Zeigt der TV sein Pairing selbst an? Dann keine eigene QR-Ansicht.
    isNativeCast(state) {
      return state.strategy === 'lg-native-cast';
    },
    // Fertige WiFi-QR-Nutzlast aus den aktuellen SoftAP-Daten.
    getWifiQrPayload(state) {
      return Cast.buildWifiQrPayload(state.softAP);
    },
    // Alles, was die Info-/Diagnose-Seite ueber Cast anzeigt.
    getDiagnostics(state) {
      return Object.assign(Cast.getDiagnostics(), {
        strategy: state.strategy,
        capabilities: state.capabilities,
        // Der SoftAP-Zustand samt ROHWERT des letzten Ereignisses. Ohne den
        // laesst sich am Geraet nicht unterscheiden, ob der TV gar nichts
        // meldet, ob die Nutzlast anders aussieht als erwartet, oder ob nur
        // die Anzeige haengt - genau die Frage, die sonst nur die Konsole
        // beantwortet.
        pairing: state.pairing,
        // Auf LG haengt daran, ob das Loeschen ueberhaupt etwas bewirkt -
        // siehe Cast/LG.js isClearCredentialsVerified.
        clearVerified: Cast.isClearCredentialsVerified(),
        snapshotTaken: state.snapshotTaken,
        snapshotReason: state.snapshotReason,
      });
    },
  },
};



// WEBPACK FOOTER //
// ./src/store/modules/cast.js